import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import {
  Upload, X, Share2, QrCode, CheckCircle, Loader, Globe, Plus, Trash2,
  ArrowUp, ArrowDown, ChevronDown, ChevronUp, Clock, MapPin, ChevronLeft, ChevronRight,
  Rss, Mail, Send, Pencil, FileText, Type, AlignLeft, Circle, CheckSquare, Copy,
  User, Smartphone, Image as ImageIcon, Info, RefreshCw, Sparkles,
} from 'lucide-react';
import { RichTextEditor } from '../components/RichTextEditor';
import { SafeHtml } from '../components/SafeHtml';
import SubcategoryPicker from '../components/admin/SubcategoryPicker';
import { InterestIcon } from '../components/admin/AdminInterests';
import {
  updateChangemakerActivity, publishChangemakerActivity, uploadActivityMedia, uploadUserImage, getInterests,
  getOrgActivityUpdates, createOrgActivityUpdate, updateOrgActivityUpdate,
  deleteOrgActivityUpdate, sendOrgActivityUpdateEmail, getOrgUpdateEmailPreview,
  getGoogleFormsAuthUrl, confirmGoogleFormsSync, turnOffGoogleFormsSync,
  selectGoogleFormsForm, triggerGoogleFormsSync, getGoogleFormsSyncStatus,
  getActivityQRCode, regenerateActivityQRCode,
  uploadEventPhoto, adminUploadEventPhoto,
} from '../api/api';
import { formatDateRange } from '../utils/dates';
import ActivityEditTutorial from '../components/ActivityEditTutorial';
import { openWhatsApp } from '../components/ShareButton';
import GoogleFormsSyncModal from '../components/GoogleFormsSyncModal';
import GoogleFormsPickerModal from '../components/GoogleFormsPickerModal';
import GoogleFormEmbed from '../components/GoogleFormEmbed';
import ActivityCollaboratorSection from '../components/ActivityCollaboratorSection';
import CollaboratorInviteModal from '../components/CollaboratorInviteModal';
import { useChangemakerAuth } from '../context/ChangemakerAuthContext';

// ── Registration form constants ───────────────────────────────────────────────

const CUSTOM_FIELD_TYPES = [
  { value: 'text',         label: 'Short Text',      icon: 'type'          },
  { value: 'paragraph',   label: 'Paragraph',        icon: 'align-left'    },
  { value: 'radio',       label: 'Multiple Choice',  icon: 'circle'        },
  { value: 'checkbox',    label: 'Checkbox',         icon: 'check-square'  },
  { value: 'dropdown',    label: 'Dropdown',         icon: 'chevron-down'  },
  { value: 'image_upload',label: 'Upload Image',     icon: 'image'         },
  { value: 'instruction', label: 'Instruction',      icon: 'info'          },
];
const HAS_OPTIONS = (type) => ['dropdown', 'radio', 'checkbox'].includes(type);
const DEFAULT_IDS = new Set(['default_name', 'default_phone', 'default_email']);

function makeDefaultRegFields() {
  return [
    { id: 'default_name',  name: 'Name',     placeholder: 'What is Your Name',     mandatory: true,  type: 'name'     },
    { id: 'default_phone', name: 'Phone No', placeholder: 'What is Your Phone No', mandatory: true,  type: 'phone'    },
    { id: 'default_email', name: 'Email Id', placeholder: 'What is Your Email Id', mandatory: true,  type: 'email_id' },
  ];
}
function initDefaultRegFields(activity) {
  const defaults = makeDefaultRegFields();
  const saved = activity?.registration_form_fields;
  if (!saved?.length) return defaults;
  const savedDefs = saved.filter((f) => DEFAULT_IDS.has(f.id));
  if (!savedDefs.length) return defaults;
  const savedName  = savedDefs.find((f) => f.id === 'default_name');
  const nameField  = { ...defaults[0], placeholder: savedName?.placeholder ?? defaults[0].placeholder };
  const savedNonName = savedDefs.filter((f) => f.id !== 'default_name');
  const rest = savedNonName.map((sf) => {
    const def = defaults.find((d) => d.id === sf.id);
    return def ? { ...def, placeholder: sf.placeholder ?? def.placeholder, mandatory: sf.mandatory ?? def.mandatory } : null;
  }).filter(Boolean);
  for (const def of defaults.slice(1)) {
    if (!rest.find((f) => f.id === def.id)) rest.push(def);
  }
  return [nameField, ...rest];
}
function initCustomRegFields(activity) {
  const saved = activity?.registration_form_fields;
  if (!saved?.length) return [];
  return saved.filter((f) => !DEFAULT_IDS.has(f.id)).map((f) => ({
    id: f.id, type: f.type || 'text', name: f.name || '',
    placeholder: f.placeholder || '', mandatory: f.mandatory || false,
    options: Array.isArray(f.options) ? f.options : [],
    instruction_image_url: f.instruction_image_url || null,
  }));
}
function makeCustomField() {
  return {
    id: `cf_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    type: 'text', name: '', placeholder: '', mandatory: false, options: [],
  };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

// Toggle switch for Required state
const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex w-9 h-5 rounded-full transition-colors flex-shrink-0 focus:outline-none ${
      checked ? 'bg-green-400' : 'bg-gray-200'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-90'}`}
    aria-pressed={checked}
    disabled={disabled}
  >
    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-150 ${
      checked ? 'translate-x-[18px]' : 'translate-x-0.5'
    }`} />
  </button>
);

// Icon for each field type
const FieldTypeIcon = ({ type, size = 11 }) => {
  const cls = `flex-shrink-0 text-gray-400`;
  if (type === 'text')         return <Type size={size} className={cls} />;
  if (type === 'paragraph')    return <AlignLeft size={size} className={cls} />;
  if (type === 'radio')        return <Circle size={size} className={cls} />;
  if (type === 'checkbox')     return <CheckSquare size={size} className={cls} />;
  if (type === 'dropdown')     return <ChevronDown size={size} className={cls} />;
  if (type === 'image_upload') return <ImageIcon size={size} className={cls} />;
  if (type === 'instruction')  return <Info size={size} className={cls} />;
  if (type === 'name')         return <User size={size} className={cls} />;
  if (type === 'phone')        return <Smartphone size={size} className={cls} />;
  if (type === 'email_id')     return <Mail size={size} className={cls} />;
  return <Type size={size} className={cls} />;
};

// Label shown in the locked "Type" cell for default fields
const DEFAULT_TYPE_LABEL = { name: 'Name', phone: 'Phone', email_id: 'Email ID' };

// Disabled input preview for view mode
const DisabledInput = ({ type, options = [], placeholder, content }) => {
  const cls = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-xs text-gray-400 bg-gray-50 cursor-not-allowed';
  if (type === 'instruction') return null; // Instruction content is shown as the field itself
  if (type === 'image_upload')
    return (
      <div className="border border-dashed border-gray-200 rounded-lg px-3 py-2.5 flex items-center gap-2 bg-gray-50">
        <ImageIcon size={13} className="text-gray-300 flex-shrink-0" />
        <span className="text-xs text-gray-400">Upload Image · max 5 MB</span>
      </div>
    );
  if (type === 'paragraph')
    return <textarea rows={2} className={cls} placeholder={placeholder || 'Answer'} disabled />;
  if (type === 'dropdown')
    return (
      <select className={cls} disabled>
        <option>{placeholder || 'Select…'}</option>
      </select>
    );
  if (type === 'radio')
    return (
      <div className="space-y-1.5">
        {(options.length > 0 ? options : [{ id: '_', value: 'Option 1' }]).filter(o => o.value?.trim()).map(o => (
          <label key={o.id} className="flex items-center gap-2 text-xs text-gray-400 cursor-not-allowed">
            <input type="radio" disabled className="accent-green-400 w-3.5 h-3.5 flex-shrink-0" />
            {o.value}
          </label>
        ))}
      </div>
    );
  if (type === 'checkbox')
    return (
      <div className="space-y-1.5">
        {(options.length > 0 ? options : [{ id: '_', value: 'Option 1' }]).filter(o => o.value?.trim()).map(o => (
          <label key={o.id} className="flex items-center gap-2 text-xs text-gray-400 cursor-not-allowed">
            <input type="checkbox" disabled className="accent-green-400 w-3.5 h-3.5 flex-shrink-0" />
            {o.value}
          </label>
        ))}
      </div>
    );
  // Default: text input (name, phone, email_id, text, short text)
  if (type === 'phone')
    return (
      <div className="flex items-stretch overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
        <span className="flex items-center px-2.5 bg-gray-100 border-r border-gray-200 text-xs text-gray-400 select-none">+91</span>
        <input type="text" disabled placeholder={placeholder || 'Answer'} className="flex-1 px-2.5 py-2 text-xs text-gray-400 bg-gray-50 cursor-not-allowed focus:outline-none" />
      </div>
    );
  return <input type="text" disabled placeholder={placeholder || 'Answer'} className={cls} />;
};

const SecLabel = ({ children }) => (
  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{children}</p>
);

// Floating-label field (border disappears when has value + not focused)
function FloatField({ label, value, onChange, multiline, rows, required, ...rest }) {
  const [focused, setFocused] = useState(false);
  const hasValue = !!(value && String(value).trim() !== '');
  const labelUp  = focused || hasValue;

  return (
    <div className={`relative rounded-xl transition-all duration-150 ${
      hasValue && !focused ? '' : `border ${focused ? 'border-gray-300' : 'border-gray-200'}`
    }`}>
      <span className={`absolute left-3 pointer-events-none transition-all duration-150 z-10 ${
        labelUp ? 'top-1.5 text-[11px] text-gray-400 font-medium' : 'top-1/2 -translate-y-1/2 text-sm text-gray-400'
      }`}>{label}{required && <span className="text-red-500 ml-0.5">*</span>}</span>
      {multiline ? (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          rows={rows || 3}
          className={`w-full bg-transparent focus:outline-none px-3 resize-none text-sm text-gray-800 ${labelUp ? 'pt-5 pb-2.5' : 'py-3'}`}
          {...rest}
        />
      ) : (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className={`w-full bg-transparent focus:outline-none px-3 text-sm text-gray-800 ${labelUp ? 'pt-5 pb-2.5' : 'py-3'}`}
          {...rest}
        />
      )}
    </div>
  );
}

// Float-style date/time field (label above, border disappears when has value + blur)
function FloatDateField({ label, value, onChange, type = 'date', required, ...rest }) {
  const [focused, setFocused] = useState(false);
  const hasValue = !!(value && String(value).trim() !== '');
  return (
    <div>
      <label className="text-[11px] text-gray-400 mb-0.5 block font-medium">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <div className={`rounded-xl transition-all duration-150 ${
        hasValue && !focused ? '' : `border ${focused ? 'border-gray-300' : 'border-gray-200'}`
      }`}>
        <input
          type={type}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="w-full bg-transparent focus:outline-none px-3 py-2.5 text-sm text-gray-700"
          {...rest}
        />
      </div>
    </div>
  );
}

// ── Time helpers ──────────────────────────────────────────────────────────────

function addDay(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
}

function to12h(timeStr) {
  if (!timeStr) return { hour: '', minute: '', ampm: 'AM' };
  const [hStr, mStr] = timeStr.split(':');
  const h = parseInt(hStr, 10) || 0;
  const m = parseInt(mStr, 10) || 0;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return { hour, minute: m, ampm };
}

function to24h(hour, minute, ampm) {
  let h = parseInt(hour, 10);
  const m = parseInt(minute, 10);
  if (isNaN(h)) return '';
  h = Math.max(1, Math.min(12, h));
  const safeM = isNaN(m) ? 0 : Math.max(0, Math.min(59, m));
  if (ampm === 'AM') { if (h === 12) h = 0; }
  else { if (h !== 12) h += 12; }
  return `${String(h).padStart(2, '0')}:${String(safeM).padStart(2, '0')}`;
}

function formatTime12h(timeStr) {
  if (!timeStr) return '';
  const { hour, minute, ampm } = to12h(timeStr);
  if (hour === '') return '';
  return `${hour}:${String(minute).padStart(2, '0')} ${ampm}`;
}

// ── AM/PM Time Picker ─────────────────────────────────────────────────────────

function AmPmTimePicker({ label, value, onChange }) {
  const parsed = to12h(value);
  const [rawHour, setRawHour] = useState(parsed.hour !== '' ? String(parsed.hour) : '');
  const [rawMinute, setRawMinute] = useState(parsed.minute !== '' ? String(parsed.minute).padStart(2, '0') : '');
  const [ampm, setAmpm] = useState(parsed.ampm);

  // Sync when parent value changes externally
  const prevValueRef = useRef(value);
  useEffect(() => {
    if (value !== prevValueRef.current) {
      prevValueRef.current = value;
      const p = to12h(value);
      setRawHour(p.hour !== '' ? String(p.hour) : '');
      setRawMinute(p.minute !== '' ? String(p.minute).padStart(2, '0') : '');
      setAmpm(p.ampm);
    }
  }, [value]);

  const commit = (h, m, ap) => {
    const ph = parseInt(h, 10);
    if (isNaN(ph) || h === '') { onChange(''); return; }
    const clamped = Math.max(1, Math.min(12, ph));
    const pm = parseInt(m, 10);
    onChange(to24h(clamped, isNaN(pm) ? 0 : pm, ap));
  };

  const handleHourBlur = () => {
    const h = parseInt(rawHour, 10);
    if (!rawHour || isNaN(h)) { setRawHour(''); onChange(''); return; }
    const clamped = Math.max(1, Math.min(12, h));
    setRawHour(String(clamped));
    commit(clamped, rawMinute, ampm);
  };

  const handleMinuteBlur = () => {
    const m = parseInt(rawMinute, 10);
    const h = parseInt(rawHour, 10);
    if (isNaN(h) || !rawHour) return;
    const clamped = isNaN(m) ? 0 : Math.max(0, Math.min(59, m));
    setRawMinute(String(clamped).padStart(2, '0'));
    commit(rawHour, clamped, ampm);
  };

  const toggleAmPm = () => {
    const newAp = ampm === 'AM' ? 'PM' : 'AM';
    setAmpm(newAp);
    commit(rawHour, rawMinute, newAp);
  };

  return (
    <div>
      <label className="text-[11px] text-gray-400 mb-0.5 block font-medium">{label}</label>
      <div className={`inline-flex items-center rounded-xl border bg-white ${value ? 'border-gray-200' : 'border-gray-200'}`}>
        <input
          type="text"
          inputMode="numeric"
          maxLength={2}
          value={rawHour}
          placeholder="HH"
          onChange={(e) => setRawHour(e.target.value.replace(/\D/g, '').slice(0, 2))}
          onBlur={handleHourBlur}
          className="w-11 text-center py-2.5 text-sm text-gray-700 bg-transparent focus:outline-none tabular-nums"
          data-testid={`time-hour-${(label || '').replace(/\s+/g, '-').toLowerCase()}`}
        />
        <span className="text-gray-300 font-bold text-sm select-none">:</span>
        <input
          type="text"
          inputMode="numeric"
          maxLength={2}
          value={rawMinute}
          placeholder="MM"
          onChange={(e) => setRawMinute(e.target.value.replace(/\D/g, '').slice(0, 2))}
          onBlur={handleMinuteBlur}
          className="w-11 text-center py-2.5 text-sm text-gray-700 bg-transparent focus:outline-none tabular-nums"
          data-testid={`time-minute-${(label || '').replace(/\s+/g, '-').toLowerCase()}`}
        />
        <button
          type="button"
          onClick={toggleAmPm}
          className="ml-1 mr-1.5 my-1 px-2 py-0.5 text-[11px] font-bold rounded-lg bg-gray-100 text-gray-600 hover:bg-green-50 hover:text-green-700 transition-colors select-none min-w-[32px]"
          data-testid={`time-ampm-${(label || '').replace(/\s+/g, '-').toLowerCase()}`}
        >
          {ampm}
        </button>
      </div>
    </div>
  );
}

// ── Custom field-type dropdown (icons in options + chevron) ───────────────────
// Uses a React portal so it escapes any overflow-hidden parent containers

function FieldTypeDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, minWidth: 160 });
  const btnRef = useRef(null);
  const selected = CUSTOM_FIELD_TYPES.find((t) => t.value === value) || CUSTOM_FIELD_TYPES[0];

  const openMenu = () => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + 4, left: rect.left, minWidth: Math.max(rect.width, 160) });
    setOpen(true);
  };

  const toggleMenu = () => {
    if (open) { setOpen(false); return; }
    openMenu();
  };

  useEffect(() => {
    if (!open) return;
    const closeOnOutside = (e) => { if (!btnRef.current?.contains(e.target)) setOpen(false); };
    // Close when page scrolls so dropdown doesn't float away from its anchor
    const closeOnScroll = () => setOpen(false);
    document.addEventListener('mousedown', closeOnOutside);
    window.addEventListener('scroll', closeOnScroll, true);
    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      window.removeEventListener('scroll', closeOnScroll, true);
    };
  }, [open]);

  return (
    <div className="relative flex-shrink-0" style={{ minWidth: 148 }}>
      <button
        ref={btnRef}
        type="button"
        onClick={toggleMenu}
        className="flex items-center gap-1.5 w-full pl-2.5 pr-2 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-green-300"
      >
        <FieldTypeIcon type={value} size={11} />
        <span className="flex-1 text-left truncate">{selected.label}</span>
        <ChevronDown size={11} className={`text-gray-400 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && createPortal(
        <div
          style={{ position: 'fixed', top: pos.top, left: pos.left, minWidth: pos.minWidth, zIndex: 9999 }}
          className="bg-white border border-gray-200 rounded-xl shadow-lg py-1"
        >
          {CUSTOM_FIELD_TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange(t.value); setOpen(false); }}
              className={`flex items-center gap-2 w-full px-3 py-2 text-xs transition-colors ${
                value === t.value ? 'bg-green-50 text-green-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FieldTypeIcon type={t.value} size={11} />
              <span>{t.label}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

// Registration — Yes / No only
function ThreeOptionPicker({ value, onChange }) {
  const opts = [{ v: 'yes', label: 'Yes' }, { v: 'no', label: 'No' }];
  return (
    <div className="flex gap-2 mt-2">
      {opts.map((opt) => (
        <label key={opt.v} className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-xl border-2 cursor-pointer transition-all ${value === opt.v ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}>
          <input type="radio" checked={value === opt.v} onChange={() => onChange(opt.v)} className="accent-green-600" />
          <span className="text-sm font-semibold text-gray-700">{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

// QR code section (inline, appears below button)
function QRSection({ url, slug }) {
  const [open, setOpen] = useState(false);

  const downloadPNG = () => {
    const canvas = document.getElementById('edit-qr-canvas')?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `${slug || 'activity'}-qr.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };
  const downloadSVG = () => {
    const svgEl = document.getElementById('edit-qr-svg')?.querySelector('svg');
    if (!svgEl) return;
    const serializer = new XMLSerializer();
    const blob = new Blob([serializer.serializeToString(svgEl)], { type: 'image/svg+xml' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${slug || 'activity'}-qr.svg`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div>
      <button onClick={() => setOpen((v) => !v)} data-testid="generate-qr-btn"
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
        <QrCode size={15} />
        {open ? 'Hide QR Code' : 'Generate QR Code'}
      </button>
      {open && (
        <div className="mt-3 p-4 bg-gray-50 rounded-xl border border-gray-200 flex flex-col items-center gap-3">
          <p className="text-xs text-gray-500 text-center">Scan to open this activity page</p>
          <div id="edit-qr-canvas" className="bg-white p-2 rounded-lg">
            <QRCodeCanvas value={url} size={180} bgColor="#ffffff" fgColor="#111827" />
          </div>
          <div id="edit-qr-svg" style={{ display: 'none' }}>
            <QRCodeSVG value={url} size={180} />
          </div>
          <div className="flex gap-2">
            <button onClick={downloadPNG} data-testid="qr-download-png"
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-900 text-white hover:bg-gray-700 transition-colors">
              Download PNG
            </button>
            <button onClick={downloadSVG} data-testid="qr-download-svg"
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors">
              Download SVG
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── VerifyParticipantsSection ─────────────────────────────────────────────────
function VerifyParticipantsSection({
  activityId, activitySlug,
  verificationEnabled, setVerificationEnabled,
  verificationType, setVerificationType,
  isEditMode,
  locIsOnline,
  hasMapInfo,
  qrFetchKey,
  inspireCardType, setInspireCardType,
  eventPhotoUrl, setEventPhotoUrl,
  activityDate,
  isAdmin,
}) {
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef(null);

  const qrBlockedOnline   = locIsOnline;
  const qrBlockedNoCoords = !locIsOnline && !hasMapInfo;
  const qrSelectable      = !qrBlockedOnline && !qrBlockedNoCoords;

  // Fetch (or auto-create) QR token whenever conditions are valid
  useEffect(() => {
    if (!verificationEnabled || verificationType !== 'qr_code' || !activityId || !qrSelectable) {
      setQrData(null);
      return;
    }
    let cancelled = false;
    setQrLoading(true);
    getActivityQRCode(activityId)
      .then((res) => { if (!cancelled) setQrData(res.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setQrLoading(false); });
    return () => { cancelled = true; };
  }, [verificationEnabled, verificationType, activityId, qrSelectable, qrFetchKey]);

  const handleRegenerateQR = async () => {
    try {
      setQrLoading(true);
      const res = await regenerateActivityQRCode(activityId);
      setQrData(res.data);
      toast.success('New QR code generated. The previous code is now invalid.');
    } catch {
      toast.error('Failed to generate new QR code');
    } finally {
      setQrLoading(false);
    }
  };

  const downloadVerifyQR = () => {
    const canvas = document.getElementById('verify-qr-canvas')?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `${activitySlug || 'activity'}-checkin-qr.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  // Photo upload handler
  const handlePhotoUpload = async (file) => {
    if (!file) return;
    setPhotoUploading(true);
    try {
      const fn = isAdmin ? adminUploadEventPhoto : uploadEventPhoto;
      const res = await fn(activityId, file);
      setEventPhotoUrl(res.data.url);
      toast.success('Event photo uploaded.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed.');
    } finally {
      setPhotoUploading(false);
    }
  };

  // Can the org user upload the photo yet? (after activity start date)
  const canUploadPhoto = isAdmin || (() => {
    if (!activityDate) return true;
    try {
      return new Date() >= new Date(activityDate);
    } catch { return true; }
  })();

  return (
    <div className="mt-0 px-4 py-6 border-t border-gray-100" data-testid="ep-verify-participants-section">
      <div className="flex items-center gap-2 mb-4">
        <CheckCircle size={15} className="text-emerald-600" />
        <h2 className="text-base font-bold text-gray-800">Verify Participants</h2>
      </div>

      {/* ─ Enable toggle ─ */}
      <div className="mb-4">
        <p className="text-sm font-semibold text-gray-700 mb-2">Do you want to verify participants?</p>
        {isEditMode ? (
          <div className="flex gap-2">
            {[{ v: false, l: 'No' }, { v: true, l: 'Yes' }].map(({ v, l }) => (
              <button
                key={l}
                type="button"
                onClick={() => { setVerificationEnabled(v); if (v) setVerificationType('manual'); else setVerificationType(null); }}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                  verificationEnabled === v
                    ? 'bg-green-600 text-white border-green-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
                data-testid={`ep-verify-enable-${l.toLowerCase()}`}
              >{l}</button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-700">{verificationEnabled ? 'Yes' : 'No'}</p>
        )}
      </div>

      {/* ─ Type picker (only when enabled) ─ */}
      {verificationEnabled && (
        <>
          <div className="mb-3">
            <p className="text-sm font-semibold text-gray-700 mb-2">How do you want to verify?</p>
            {isEditMode ? (
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={qrBlockedOnline || qrBlockedNoCoords}
                  onClick={() => qrSelectable && setVerificationType('qr_code')}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                    verificationType === 'qr_code'
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : (qrBlockedOnline || qrBlockedNoCoords)
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid="ep-verify-type-qr"
                >QR Code</button>
                <button
                  type="button"
                  onClick={() => setVerificationType('manual')}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                    verificationType === 'manual'
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid="ep-verify-type-manual"
                >Manually</button>
              </div>
            ) : (
              <p className="text-sm text-gray-700">
                {verificationType === 'qr_code' ? 'QR Code' : verificationType === 'manual' ? 'Manual' : '—'}
              </p>
            )}
          </div>

          {/* ─ Warnings ─ */}
          {verificationType !== 'manual' && qrBlockedOnline && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl mb-3">
              <Info size={14} className="text-amber-500 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700">QR check-in is not available for online activities.</p>
            </div>
          )}
          {verificationType !== 'manual' && !qrBlockedOnline && qrBlockedNoCoords && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl mb-3">
              <Info size={14} className="text-amber-500 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700">Add a Google Maps location link first to use QR verification.</p>
            </div>
          )}

          {/* ─ QR code display ─ */}
          {verificationType === 'qr_code' && qrSelectable && (
            <div className="mt-3">
              {qrLoading && (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                  <Loader size={14} className="animate-spin" />
                  Loading QR code…
                </div>
              )}
              {!qrLoading && qrData?.qr_url && (
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 inline-flex flex-col items-center gap-3">
                  <p className="text-xs text-gray-500 text-center">
                    Organisers: download and display this at your event venue.
                  </p>
                  <div id="verify-qr-canvas" className="bg-white p-2 rounded-lg shadow-sm">
                    <QRCodeCanvas value={qrData.qr_url} size={180} bgColor="#ffffff" fgColor="#111827" includeMargin={true} />
                  </div>
                  <div className="flex gap-2 flex-wrap justify-center">
                    <button
                      type="button"
                      onClick={downloadVerifyQR}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-900 text-white hover:bg-gray-700 transition-colors"
                      data-testid="ep-verify-qr-download"
                    >
                      <Upload size={11} />
                      Download PNG
                    </button>
                    <button
                      type="button"
                      onClick={handleRegenerateQR}
                      disabled={qrLoading}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                      data-testid="ep-verify-qr-regenerate"
                    >
                      <RefreshCw size={11} />
                      Generate New QR
                    </button>
                  </div>
                  {qrData.generated_at && (
                    <p className="text-xs text-gray-400">
                      Generated {new Date(qrData.generated_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ─ Inspire Others (distinct section) ─ */}
      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={15} className="text-yellow-600" />
          <h2 className="text-base font-bold text-gray-800">Inspire Others</h2>
        </div>

        <p className="text-sm font-semibold text-gray-700 mb-2">Participants will receive following type of Inspire Card</p>
        {isEditMode ? (
          <div className="flex flex-wrap gap-2">
            {[
              { v: 'generic',              l: 'Generic Card' },
              { v: 'photo_from_organiser', l: 'Photo from Organiser' },
              { v: 'certificate',          l: 'Certificate' },
            ].map(({ v, l }) => (
              <button
                key={v}
                type="button"
                onClick={() => setInspireCardType(v)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                  inspireCardType === v
                    ? 'bg-green-600 text-white border-green-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
                data-testid={`ep-inspire-type-${v}`}
              >{l}</button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-700">
            {inspireCardType === 'generic' ? 'Generic Card'
              : inspireCardType === 'photo_from_organiser' ? 'Photo from Organiser'
              : inspireCardType === 'certificate' ? 'Certificate' : '—'}
          </p>
        )}

        {/* Certificate + no-verification warning */}
        {isEditMode && inspireCardType === 'certificate' && !verificationEnabled && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl mt-3" data-testid="ep-cert-no-verify-warning">
            <Info size={14} className="text-amber-500 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-700">Certificate requires participant verification to be enabled. Please enable verification above, or choose a different card type.</p>
          </div>
        )}

        {/* Photo from Organiser upload */}
        {inspireCardType === 'photo_from_organiser' && activityId && (
          <div className="mt-3" data-testid="ep-event-photo-upload">
            {!canUploadPhoto ? (
              <p className="text-xs text-gray-400">Upload will be available after the activity starts.</p>
            ) : (
              <div className="flex items-center gap-3">
                {eventPhotoUrl ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <img src={eventPhotoUrl} alt="Event" className="h-12 w-12 rounded-lg object-cover border border-gray-200" />
                    <div>
                      <p className="text-xs text-green-700 font-semibold">Photo uploaded</p>
                      <button
                        type="button"
                        onClick={() => photoInputRef.current?.click()}
                        className="text-xs text-gray-500 underline mt-0.5"
                        data-testid="ep-event-photo-replace"
                      >Replace</button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={photoUploading}
                    onClick={() => photoInputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    data-testid="ep-event-photo-upload-btn"
                  >
                    {photoUploading ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />}
                    {photoUploading ? 'Uploading…' : 'Upload photo of the event'}
                  </button>
                )}
                <input
                  ref={photoInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  className="hidden"
                  onChange={(e) => { if (e.target.files?.[0]) handlePhotoUpload(e.target.files[0]); e.target.value = ''; }}
                  data-testid="ep-event-photo-input"
                />
              </div>
            )}
            <p className="text-xs text-gray-400 mt-2">JPG, PNG, WebP · max 5 MB</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Section names for progress bar ────────────────────────────────────────────
const SECTION_LABELS = ['Activity Details', 'Registration Form', 'Publish & Share'];

// Auto-resizing heading textarea — shows border on focus, multi-line support
function HeadingInput({ value, onChange }) {
  const [focused, setFocused] = useState(false);
  const taRef = useRef(null);

  useEffect(() => {
    if (taRef.current) {
      taRef.current.style.height = 'auto';
      taRef.current.style.height = taRef.current.scrollHeight + 'px';
    }
  }, [value]);

  return (
    <textarea
      ref={taRef}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      rows={1}
      className={`w-full text-2xl font-bold text-gray-900 leading-tight bg-transparent resize-none overflow-hidden focus:outline-none rounded-xl px-2 py-1.5 -mx-2 transition-all duration-150 ${
        focused ? 'ring-1 ring-gray-300' : 'ring-0'
      }`}
      placeholder="Activity Heading"
      style={{ minHeight: '44px' }}
      data-testid="edit-name"
    />
  );
}

// ── Update Email Modal ────────────────────────────────────────────────────────

function UpdateEmailModal({ update, activityId, onClose, onSent }) {
  const [allUsers, setAllUsers]     = useState(false);
  const [newUsers, setNewUsers]     = useState(false);
  const [others, setOthers]         = useState(false);
  const [otherEmails, setOtherEmails] = useState('');
  const [sending, setSending]       = useState(false);
  const [sentResult, setSentResult] = useState(null);
  const [preview, setPreview]       = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);

  useEffect(() => {
    getOrgUpdateEmailPreview(activityId, update.id)
      .then(r => setPreview(r.data))
      .catch(() => setPreview(null))
      .finally(() => setPreviewLoading(false));
  }, [activityId, update.id]);

  // Active conditions
  const allUsersEnabled = !previewLoading && (preview?.all_users ?? 0) > 0;
  const newUsersEnabled = !previewLoading && !!preview?.last_email_sent_at && (preview?.new_users ?? 0) > 0;

  // Mutual exclusion: All Users ↔ New Users
  const toggleAllUsers = (checked) => {
    setAllUsers(checked);
    if (checked) setNewUsers(false);
  };
  const toggleNewUsers = (checked) => {
    setNewUsers(checked);
    if (checked) setAllUsers(false);
  };

  const validOthers = otherEmails
    .split(/[\n,;]+/)
    .map(e => e.trim())
    .filter(e => e.includes('@') && e.includes('.'));

  const canSend = !sending && !sentResult && (
    (allUsers && allUsersEnabled) ||
    (newUsers && newUsersEnabled) ||
    (others && validOthers.length > 0)
  );

  const handleSend = async () => {
    setSending(true);
    try {
      // Determine base mode: all_users beats new_users beats others
      let mode;
      if (allUsers)      mode = 'all_users';
      else if (newUsers) mode = 'new_users';
      else               mode = 'others';
      const extra_emails = others ? validOthers : [];
      const res = await sendOrgActivityUpdateEmail(activityId, update.id, { mode, extra_emails });
      setSentResult(res.data);
      onSent(update.id, res.data.sent_at);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const allCount  = previewLoading ? '…' : (preview?.all_users ?? 0);
  const newCount  = previewLoading ? '…' : (preview?.new_users ?? 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="update-email-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900 text-base">Send Update Email</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors" data-testid="email-modal-close">
            <X size={18} />
          </button>
        </div>

        {sentResult ? (
          /* ── Success ── */
          <div className="p-8 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle size={22} className="text-green-600" />
            </div>
            <p className="font-bold text-gray-900 mb-1">Email Sent!</p>
            <p className="text-sm text-gray-500">
              Sent to <strong>{sentResult.recipient_count}</strong> recipient{sentResult.recipient_count !== 1 ? 's' : ''}.
            </p>
            <button onClick={onClose} className="mt-4 px-6 py-2.5 bg-green-700 text-white text-sm font-semibold rounded-xl hover:bg-green-800 transition-colors">
              Done
            </button>
          </div>
        ) : (
          <div className="px-5 py-4 space-y-3">

            {/* All Users */}
            <label className={`flex items-start gap-3 ${!allUsersEnabled || newUsers ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
              data-testid="mode-all-users">
              <input
                type="checkbox"
                checked={allUsers}
                disabled={!allUsersEnabled || newUsers}
                onChange={e => toggleAllUsers(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-green-600 flex-shrink-0"
              />
              <span className="text-sm text-gray-800">
                All Users{' '}
                <span className="text-gray-500">({allCount} registered participants and interested users)</span>
              </span>
            </label>

            {/* New Users */}
            <label className={`flex items-start gap-3 ${!newUsersEnabled || allUsers ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
              data-testid="mode-new-users">
              <input
                type="checkbox"
                checked={newUsers}
                disabled={!newUsersEnabled || allUsers}
                onChange={e => toggleNewUsers(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-green-600 flex-shrink-0"
              />
              <span className="text-sm text-gray-800">
                New Users{' '}
                <span className="text-gray-500">({newCount} users who joined since last email)</span>
              </span>
            </label>

            {/* Others */}
            <div data-testid="mode-others">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={others}
                  onChange={e => setOthers(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-green-600 flex-shrink-0"
                />
                <span className="text-sm text-gray-800">
                  Others <span className="text-gray-500">(add email ids)</span>
                </span>
              </label>
              {others && (
                <div className="mt-2 ml-7">
                  <textarea
                    value={otherEmails}
                    onChange={e => setOtherEmails(e.target.value)}
                    placeholder="Enter emails separated by comma, semicolon, or newline"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-400 resize-none"
                    rows={3}
                    autoFocus
                    data-testid="other-emails-input"
                  />
                  {validOthers.length > 0 && (
                    <p className="text-xs text-green-700 font-medium mt-1">
                      {validOthers.length} valid email{validOthers.length !== 1 ? 's' : ''} detected
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 text-sm rounded-xl hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={!canSend}
                className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-green-700 hover:bg-green-800 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50"
                data-testid="email-send-btn"
              >
                {sending
                  ? <><Loader size={14} className="animate-spin" /> Sending…</>
                  : <><Send size={14} /> Send</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ── FormsSyncBlock ────────────────────────────────────────────────────────────
// Compact sync status + action block — always left-aligned, solid-border button

function FormsSyncBlock({ formsSync, onSync, onReSync, onTurnOff, disabled = false }) {
  const status = formsSync?.sync_status;
  const isActive = status === 'active' && formsSync?.enabled;
  const needsReauth = status === 'needs_reauth';
  const lastSync = formsSync?.last_sync_at
    ? new Date(formsSync.last_sync_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : null;
  const totalSynced = formsSync?.total_synced || 0;

  if (isActive) {
    return (
      <div className="rounded-xl bg-green-50 border border-green-200 px-3 py-2.5 mt-2" data-testid="forms-sync-active-block">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2">
            <RefreshCw size={13} className="text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold text-green-800">Registration Form Response is Synced</p>
              {lastSync && <p className="text-[11px] text-green-600 mt-0.5">Last synced: {lastSync}</p>}
              {totalSynced > 0 && <p className="text-[11px] text-gray-500 mt-0.5">{totalSynced} response{totalSynced !== 1 ? 's' : ''} imported</p>}
            </div>
          </div>
          {!disabled && (
            <div className="flex gap-1.5 flex-shrink-0">
              <button onClick={onReSync || onSync} className="text-[11px] font-semibold text-green-700 px-2 py-1 rounded-lg border border-green-300 bg-white hover:bg-green-50 transition-colors whitespace-nowrap" data-testid="forms-sync-re-sync-btn">Re-sync</button>
              <button onClick={onTurnOff} className="text-[11px] font-semibold text-red-500 px-2 py-1 rounded-lg border border-red-200 bg-white hover:bg-red-50 transition-colors whitespace-nowrap" data-testid="forms-sync-turn-off-btn">Turn Off</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (needsReauth) {
    return (
      <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 mt-2" data-testid="forms-sync-reauth-block">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <RefreshCw size={13} className="text-amber-600 flex-shrink-0" />
            <p className="text-xs font-semibold text-amber-800">Needs to be synced again</p>
          </div>
          {!disabled && (
            <button onClick={onSync} className="text-[11px] font-bold text-white bg-amber-500 hover:bg-amber-600 px-2.5 py-1 rounded-lg transition-colors whitespace-nowrap" data-testid="forms-sync-reauth-btn">Sync Responses</button>
          )}
        </div>
      </div>
    );
  }

  // Default: no sync or disabled — compact left-aligned button with solid border
  return (
    <div className="mt-2" data-testid="forms-sync-default-block">
      {formsSync?.total_synced > 0 && (
        <p className="text-[11px] text-gray-400 mb-1.5">{formsSync.total_synced} response{formsSync.total_synced !== 1 ? 's' : ''} previously synced (sync is off).</p>
      )}
      <button
        onClick={(e) => { if (!disabled) { e.stopPropagation(); e.preventDefault(); onSync(); } }}
        disabled={disabled}
        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border text-sm font-semibold transition-colors ${
          disabled
            ? 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
            : 'border-green-400 text-green-700 hover:bg-green-50'
        }`}
        data-testid="forms-sync-start-btn"
      >
        <RefreshCw size={13} />
        Sync Responses
      </button>
    </div>
  );
}


// ── Main component ────────────────────────────────────────────────────────────

export default function ActivityEditPage({ activity: initialActivity, organizer, isPublished: initialPublished, onExitEdit, onPublished, saveFn, publishFn, uploadFn, initialEditMode, onEditModeChange, isAdmin = false }) {
  const activityId   = initialActivity.id;
  const activitySlug = initialActivity.slug || activityId;
  const pageUrl      = `${window.location.origin}/activities/${activitySlug}`;

  // Determine if the current org is the main organiser — used to gate collab management
  const { cmOrg } = useChangemakerAuth();
  const isMainOrg = useMemo(() => {
    if (!initialActivity?.id) return true; // new activity
    if (initialActivity.is_collaboration) return false; // explicitly marked as collaborator
    if (cmOrg?.id && initialActivity.changemaker_id) {
      return cmOrg.id === initialActivity.changemaker_id;
    }
    return isAdmin; // admin has access when no org context
  }, [initialActivity?.id, initialActivity?.changemaker_id, initialActivity?.is_collaboration, cmOrg?.id, isAdmin]);

  // ── Field state ─────────────────────────────────────────────────────────────
  const [name, setName]                     = useState(initialActivity.name || '');
  const [description, setDescription]       = useState(initialActivity.description || '');
  const [date, setDate]                     = useState(initialActivity.date || '');
  const [dateEnd, setDateEnd]               = useState(initialActivity.date_end || '');
  const [startTime, setStartTime]           = useState(initialActivity.start_time || '');
  const [endTime, setEndTime]               = useState(initialActivity.end_time || '');
  const [locAddress, setLocAddress]         = useState(initialActivity.location?.address || '');
  const [locMapLink, setLocMapLink]         = useState(initialActivity.location?.map_link || '');
  const [locIsOnline, setLocIsOnline]       = useState(initialActivity.location?.is_online || false);
  const [thingsToBring, setThingsToBring]   = useState(initialActivity.things_to_bring || '');
  const [howToReach, setHowToReach]         = useState(initialActivity.how_to_reach || '');
  const [activityContacts, setActivityContacts] = useState(() => {
    if (initialActivity.activity_contacts?.length > 0) {
      return initialActivity.activity_contacts;
    }
    // Auto-populate from organizer contacts on first create
    if (organizer?.contact?.phones?.length > 0) {
      return organizer.contact.phones.map((phone) => ({ name: organizer.name || '', phone }));
    }
    return [];
  });
  const [areasOfInterest, setAreasOfInterest] = useState(initialActivity.areas_of_interest?.map(String) || []);
  const [mediaUrls, setMediaUrls]           = useState(initialActivity.media_urls || []);
  const [isUploading, setIsUploading]       = useState(false);

  // Registration
  const [regRequirement, setRegRequirement] = useState(
    initialActivity.registration_requirement === 'mandatory' ? 'yes'
    : initialActivity.registration_requirement === 'optional' ? 'yes'  // map legacy 'optional' → 'yes'
    : initialActivity.registration_requirement === 'no' ? 'no'
    : 'no'
  );
  const [regType, setRegType]   = useState(
    initialActivity.registration_type === 'link' ? 'link' : 'karmana'
  );
  const [regLink, setRegLink]   = useState(initialActivity.registration_link || '');
  const [regEndDate, setRegEndDate]                 = useState(initialActivity.registration_end_date || '');
  const [regEndDateModified, setRegEndDateModified] = useState(!!initialActivity.registration_end_date);
  const [defaultFields, setDefaultFields] = useState(() => initDefaultRegFields(initialActivity));
  const [customFields, setCustomFields]   = useState(() => initCustomRegFields(initialActivity));
  const [fieldErrors, setFieldErrors]     = useState(new Set());
  const [regOptionalMessage, setRegOptionalMessage] = useState('');

  // Google Forms sync state
  const [formsSync,       setFormsSync]       = useState(initialActivity.forms_sync || null);
  const [syncModalOpen,   setSyncModalOpen]   = useState(false);
  const [syncModalData,   setSyncModalData]   = useState(null); // {tempToken, headers, suggestedMapping}
  const [formsPickerData, setFormsPickerData] = useState(null); // {tempToken, forms}
  const [syncLoading,     setSyncLoading]     = useState(false);

  // Participant limit
  const [isParticipantsFixed, setIsParticipantsFixed] = useState(
    initialActivity.is_participants_fixed === true
  );
  const [maxParticipants, setMaxParticipants] = useState(
    initialActivity.max_participants ?? 50
  );

  // Participant verification
  const [verificationEnabled, setVerificationEnabled] = useState(
    initialActivity.verification_enabled === true
  );
  const [verificationType, setVerificationType] = useState(
    initialActivity.verification_type || null
  );
  const [qrFetchKey, setQrFetchKey] = useState(0);

  // Inspire card type
  const [inspireCardType, setInspireCardType] = useState(
    initialActivity.inspire_card_type || 'generic'
  );
  const [eventPhotoUrl, setEventPhotoUrl] = useState(
    initialActivity.inspire_card_event_photo_url || null
  );

  // UI state
  const [interestsOpen, setInterestsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [saveStatus, setSaveStatus]       = useState('idle');
  const [isPublished, setIsPublished]     = useState(initialPublished || initialActivity.status === 'published');
  // Start in edit mode ONLY for draft activities (create new). Published activities always start frozen.
  // `initialEditMode` prop overrides this when coming from ?mode=edit URL.
  const [isEditMode, setIsEditMode]       = useState(
    initialEditMode !== undefined
      ? initialEditMode
      : !(initialPublished || initialActivity.status === 'published')
  );
  const [publishing, setPublishing]       = useState(false);
  const [publishError, setPublishError]   = useState('');
  const [editCarouselIdx, setEditCarouselIdx] = useState(0);
  const [barIsFixed, setBarIsFixed]       = useState(true);
  const [pendingInviteCollabs, setPendingInviteCollabs] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Edit-mode helpers — also sync URL via callback when provided
  const enterEditMode = () => { setIsEditMode(true);  onEditModeChange?.(true); };
  const exitEditMode  = () => { setIsEditMode(false); onEditModeChange?.(false); };

  // ── Refs ─────────────────────────────────────────────────────────────────────
  const detailsRef      = useRef(null);
  const regRef          = useRef(null);
  const publishRef      = useRef(null);
  const contentRef      = useRef(null);
  const saveTimerRef    = useRef(null);
  const editCarouselRef = useRef(null);
  const actionBarRef    = useRef(null);

  // Auto-fill regEndDate with dateEnd||date whenever activity dates change, unless user has edited it
  useEffect(() => {
    if (!regEndDateModified && regRequirement === 'yes') {
      setRegEndDate(dateEnd || date || '');
    }
  }, [date, dateEnd, regRequirement, regEndDateModified]);

  // ── Activity Updates state ────────────────────────────────────────────────────
  const [activityUpdates, setActivityUpdates] = useState([]);
  const [updatesLoading, setUpdatesLoading] = useState(false);
  // New update form
  const [showNewUpdateForm, setShowNewUpdateForm] = useState(false);
  const [newUpdateHeading, setNewUpdateHeading] = useState('');
  const [newUpdateContent, setNewUpdateContent] = useState('');
  const [newUpdateMedia, setNewUpdateMedia] = useState([]);
  const [newUpdateMediaNames, setNewUpdateMediaNames] = useState([]);

  const [newUpdateError, setNewUpdateError] = useState('');
  // Inline edit state
  const [editingUpdateId, setEditingUpdateId] = useState(null);
  const [editUpdateHeading, setEditUpdateHeading] = useState('');
  const [editUpdateContent, setEditUpdateContent] = useState('');
  const [editUpdateMedia, setEditUpdateMedia] = useState([]);
  const [editUpdateMediaNames, setEditUpdateMediaNames] = useState([]);
  const [isUploadingEditMedia, setIsUploadingEditMedia] = useState(false);
  const [isUploadingUpdateMedia, setIsUploadingUpdateMedia] = useState(false);
  const [editUpdateSaving, setEditUpdateSaving] = useState(false);
  // Tracks update IDs saved since last Publish — Send Email disabled until publish
  const [pendingPublishUpdateIds, setPendingPublishUpdateIds] = useState(new Set());
  // Email send loading state per update id
  const [emailModalUpdate, setEmailModalUpdate] = useState(null);

  // ── Onboarding tutorial state ────────────────────────────────────────────────
  const [editTutorialStep, setEditTutorialStep] = useState(null);

  // Detect tutorial hand-off from org page (localStorage written before window.open)
  useEffect(() => {
    try {
      const raw = localStorage.getItem('karmana_tutorial');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.step === 7) {
          setEditTutorialStep(7);
          // Ensure edit mode is on so fields are editable
          setIsEditMode(true);
        }
      }
    } catch {}
  }, []); // eslint-disable-line

  // ── Tutorial: pre-fill registration form on step 9 (demo state) ────────────
  useEffect(() => {
    if (editTutorialStep === 9) {
      setRegRequirement('yes');
      setRegType('karmana');
    }
  }, [editTutorialStep]);

  // ── Sticky-until-docked action bar ──────────────────────────────────────────
  useEffect(() => {
    const el = actionBarRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setBarIsFixed(!entry.isIntersecting),
      { threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []); // eslint-disable-line

  // ── Progress rail positions ────────────────────────────────────────────────
  const [dotPositions, setDotPositions] = useState([160, 320, 480]);
  const [lineRight, setLineRight]       = useState(8);
  const [labelLeft, setLabelLeft]       = useState(0);
  const [lineBottom, setLineBottom]     = useState(0);

  // ── Interests query ──────────────────────────────────────────────────────────
  const { data: interests = [] } = useQuery({
    queryKey: ['interests'],
    queryFn: async () => (await getInterests()).data,
    staleTime: 300_000,
  });

  // ── Fetch existing updates when activity is published ─────────────────────────
  useEffect(() => {
    if (!isPublished) return;
    setUpdatesLoading(true);
    getOrgActivityUpdates(activityId)
      .then((r) => setActivityUpdates(r.data || []))
      .catch(() => {})
      .finally(() => setUpdatesLoading(false));
  }, [isPublished, activityId]); // eslint-disable-line

  // ── Scroll spy — section detection + label positions ─────────────────────────
  useEffect(() => {
    const UPDATE_MARGIN = 100; // px from top/bottom of viewport (first label top margin)
    const MIN_GAP = 44;        // minimum 2 text-line heights between labels

    const update = () => {
      const refs = [detailsRef, regRef, publishRef];
      const vh   = window.innerHeight;

      // Which section is most visible → activeSection
      let bestIdx = 0;
      let bestVis = -Infinity;
      refs.forEach((r, idx) => {
        if (!r.current) return;
        const rect = r.current.getBoundingClientRect();
        const vis  = Math.min(rect.bottom, vh) - Math.max(rect.top, 0);
        if (vis > bestVis) { bestVis = vis; bestIdx = idx; }
      });
      setActiveSection(bestIdx);

      // Dot Y positions — centered on section, clamped, min gap enforced
      const raw = refs.map((r) => {
        if (!r.current) return vh / 2;
        const rect   = r.current.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        return Math.max(UPDATE_MARGIN, Math.min(vh - UPDATE_MARGIN, center));
      });
      // Enforce min gap forward
      for (let i = 1; i < raw.length; i++) {
        if (raw[i] - raw[i - 1] < MIN_GAP) raw[i] = raw[i - 1] + MIN_GAP;
      }
      setDotPositions(raw);

      // Line X position
      if (contentRef.current) {
        const rect      = contentRef.current.getBoundingClientRect();
        const fromRight = window.innerWidth - rect.right - 40; // 40px gap from content edge
        const lr        = Math.max(4, fromRight);
        setLineRight(lr);
        setLabelLeft(window.innerWidth - lr + 10); // labels start 10px past the line
      }
      // Line bottom: tracks the site footer top — line never enters footer
      const footerEl = document.querySelector('footer');
      if (footerEl) {
        const footerRect = footerEl.getBoundingClientRect();
        // Distance from viewport bottom to footer top (0 when footer is off-screen below)
        const distFromBottom = Math.max(0, vh - footerRect.top + 1);
        setLineBottom(distFromBottom);
      }
    };

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  // Reset carousel index when mediaUrls count changes
  useEffect(() => { setEditCarouselIdx(0); }, [mediaUrls.length]);

  // ── Build autosave payload ────────────────────────────────────────────────────
  const buildPayload = useCallback(() => {
    const hasReg = regRequirement === 'yes';
    const payload = {
      name,
      description,
      date: date || null,
      date_end: dateEnd || null,
      is_ongoing: false,
      start_time: startTime || null,
      end_time: endTime || null,
      location: { address: locIsOnline ? 'Online' : locAddress, map_link: locMapLink, is_online: locIsOnline },
      areas_of_interest: areasOfInterest,
      media_urls: mediaUrls,
      things_to_bring: thingsToBring || null,
      how_to_reach: howToReach || null,
      activity_contacts: activityContacts.filter((c) => c.name || c.phone),
      registration_requirement: regRequirement === 'yes' ? 'mandatory' : 'no',
      registration_end_date: hasReg ? (regEndDate || null) : null,
    };
    if (hasReg && regType === 'link') {
      payload.registration_type = 'link';
      payload.registration_link = regLink;
    } else if (hasReg && regType === 'karmana') {
      payload.registration_type = 'karmana';
      payload.registration_link = null;
      payload.registration_form_fields = [...defaultFields, ...customFields];
    } else {
      payload.registration_type = null;
      payload.registration_link = null;
    }
    payload.is_participants_fixed = isParticipantsFixed;
    payload.max_participants = isParticipantsFixed ? (parseInt(maxParticipants, 10) || 50) : null;
    payload.verification_enabled = verificationEnabled;
    payload.verification_type = verificationEnabled ? (verificationType || null) : null;
    payload.inspire_card_type = inspireCardType;
    return payload;
  }, [name, description, date, dateEnd, startTime, endTime, locAddress, locMapLink, locIsOnline,
      areasOfInterest, mediaUrls, thingsToBring, howToReach, activityContacts, regRequirement, regType, regLink, regEndDate, defaultFields, customFields,
      isParticipantsFixed, maxParticipants, verificationEnabled, verificationType]);

  // ── Manual save ───────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (saveStatus === 'saving') return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    // Validate: check for empty custom fields (only when reg form is active)
    const emptyFields = regRequirement === 'yes' ? customFields.filter(f => {
      if (f.type === 'instruction') return !(f.name || '').replace(/<[^>]+>/g, '').trim();
      return !f.name?.trim();
    }) : [];
    if (emptyFields.length > 0) {
      toast.warning(`${emptyFields.length} form field${emptyFields.length > 1 ? 's are' : ' is'} empty. Add content to all fields before saving.`);
      return;
    }
    // Validate: if update form is open, heading is required
    if (showNewUpdateForm && newUpdateHeading.trim() === '') {
      setNewUpdateError('Please add a heading for this update before saving');
      return;
    }
    // Validate: certificate card type requires verification enabled
    if (inspireCardType === 'certificate' && !verificationEnabled) {
      toast.error('Certificate cards require participant verification to be enabled. Please enable verification or choose a different card type.');
      return;
    }
    setSaveStatus('saving');
    try {
      await (saveFn || updateChangemakerActivity)(activityId, buildPayload());
      // Save pending update if the form has content
      if (showNewUpdateForm && newUpdateHeading.trim()) {
        const res = await createOrgActivityUpdate(activityId, {
          heading: newUpdateHeading.trim(),
          content: newUpdateContent,
          media_urls: newUpdateMedia,
          media_names: newUpdateMediaNames,
        });
        const saved = res.data;
        setActivityUpdates((prev) => [...prev, saved]);
        setPendingPublishUpdateIds((prev) => new Set([...prev, saved.id]));
        setNewUpdateHeading('');
        setNewUpdateContent('');
        setNewUpdateMedia([]);
        setNewUpdateMediaNames([]);
        setNewUpdateError('');
        setShowNewUpdateForm(false);
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
      toast.success('Saved!');
      // Trigger QR re-fetch after save if QR mode is active
      if (verificationEnabled && verificationType === 'qr_code') {
        setQrFetchKey(k => k + 1);
      }
      exitEditMode();
    } catch (err) {
      setSaveStatus('idle');
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
  };

  // ── Media upload (up to 5, first 5 if more selected) ─────────────────────────
  const handleMediaUpload = async (files) => {
    const remaining = 5 - mediaUrls.length;
    if (remaining <= 0) return;

    // If admin uploads a PDF: convert pages server-side, add as images
    const fileList = Array.from(files);
    const pdfFile = isAdmin ? fileList.find((f) => f.type === 'application/pdf') : null;
    if (pdfFile) {
      setIsUploading(true);
      try {
        const { adminUploadPdfPages: uploadPdf } = await import('../api/api');
        const result = await uploadPdf(pdfFile);
        const pagesToAdd = (result.urls || []).slice(0, remaining);
        if (pagesToAdd.length === 0) toast.error('No pages could be extracted from the PDF.');
        else setMediaUrls((prev) => [...prev, ...pagesToAdd].slice(0, 5));
      } catch {
        toast.error('PDF upload failed. Make sure it is under 20 MB.');
      } finally {
        setIsUploading(false);
      }
      return;
    }

    // Normal image / video upload
    const toUpload = fileList.slice(0, remaining);
    setIsUploading(true);
    for (const file of toUpload) {
      try {
        const _upload = uploadFn || uploadActivityMedia;
        const url = await _upload(file);
        setMediaUrls((prev) => [...prev, url].slice(0, 5));
      } catch {
        toast.error('Upload failed for one file');
      }
    }
    setIsUploading(false);
  };

  const removeAllMedia = () => { setMediaUrls([]); setEditCarouselIdx(0); };

  // ── Carousel navigation ───────────────────────────────────────────────────────
  const carouselScrollTo = (idx) => {
    if (!editCarouselRef.current) return;
    editCarouselRef.current.scrollTo({ left: idx * editCarouselRef.current.offsetWidth, behavior: 'smooth' });
  };

  // ── Registration helpers ──────────────────────────────────────────────────────
  const handleRegTypeChange = (t) => { setRegType(t); setDefaultFields(makeDefaultRegFields()); setCustomFields([]); setFieldErrors(new Set()); };

  // ── Google Forms Sync handlers ─────────────────────────────────────────────

  const handleSyncResponses = async () => {
    if (!initialActivity.id) {
      toast.error('Please save the activity first before syncing.');
      return;
    }
    if (!regLink.trim()) {
      toast.error('Please enter and save a Google Forms link first.');
      return;
    }

    try {
      const { data } = await getGoogleFormsAuthUrl(initialActivity.id);
      const authUrl = data.auth_url;

      // Open OAuth popup
      const popup = window.open(
        authUrl,
        'google-forms-auth',
        'width=600,height=700,scrollbars=yes,resizable=yes'
      );

      if (!popup) {
        toast.error('Popup blocked. Please allow popups for this site and try again.');
        return;
      }

      // Listen for postMessage from callback HTML page
      const handleMessage = (event) => {
        if (!event.data || event.data.type === undefined) return;

        window.removeEventListener('message', handleMessage);

        if (event.data.type === 'GOOGLE_FORMS_ERROR') {
          toast.error(event.data.error || 'Google authorization failed.');
          return;
        }

        if (event.data.type === 'GOOGLE_FORMS_SELECT_FORM') {
          // Auto-detection failed — show form picker
          const { temp_token, forms } = event.data;
          setFormsPickerData({ tempToken: temp_token, forms: forms || [] });
          return;
        }

        if (event.data.type === 'GOOGLE_FORMS_AUTH') {
          const { temp_token, headers, suggested_mapping } = event.data;
          if (!headers || headers.length === 0) {
            toast.error('No questions found in the Google Form. Make sure the form has at least one question.');
            return;
          }
          // Merge previous saved mapping with Jev suggestions
          const previousMapping = formsSync?.field_mapping || {};
          const mergedMapping = { ...(suggested_mapping || {}) };
          ['name_column', 'email_column', 'phone_column'].forEach((field) => {
            if (previousMapping[field] && headers.includes(previousMapping[field])) {
              mergedMapping[field] = previousMapping[field];
            }
          });
          setSyncModalData({ tempToken: temp_token, headers, suggestedMapping: mergedMapping });
          setSyncModalOpen(true);
        }
      };

      window.addEventListener('message', handleMessage);

      // Clean up listener if popup was closed without completing auth
      const timer = setInterval(() => {
        if (popup.closed) {
          clearInterval(timer);
          window.removeEventListener('message', handleMessage);
        }
      }, 1000);

    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to start Google authorization.';
      toast.error(msg);
    }
  };

  const handleConfirmSync = async (fieldMapping) => {
    if (!syncModalData?.tempToken) return;
    setSyncLoading(true);
    try {
      await confirmGoogleFormsSync(initialActivity.id, syncModalData.tempToken, fieldMapping);
      setSyncModalOpen(false);
      setSyncModalData(null);
      toast.success('Sync started! Responses are being imported.');
      // Refresh sync status
      try {
        const { data } = await import('../api/api').then((m) =>
          m.getGoogleFormsSyncStatus(initialActivity.id)
        );
        setFormsSync(data.forms_sync || null);
      } catch (_) {}
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to confirm sync.';
      toast.error(msg);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleTurnOffSync = async () => {
    try {
      await turnOffGoogleFormsSync(initialActivity.id);
      setFormsSync((prev) => prev ? { ...prev, sync_status: 'disabled', enabled: false } : prev);
      toast.success('Sync turned off. Past data is preserved.');
    } catch (err) {
      toast.error('Failed to turn off sync.');
    }
  };

  // Re-sync using stored tokens — no OAuth needed when sync is already active
  const handleReSyncResponses = async () => {
    try {
      await triggerGoogleFormsSync(initialActivity.id);
      toast.success('Sync triggered! Checking for new responses...');
      // Refresh sync status after a short delay to pick up new last_sync_at
      setTimeout(async () => {
        try {
          const { data } = await getGoogleFormsSyncStatus(initialActivity.id);
          setFormsSync(data.forms_sync || null);
        } catch (_) {}
      }, 4000);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to trigger sync.';
      toast.error(msg);
    }
  };

  // Form picker: user selected a form manually after auto-detection failed
  const handleFormPickerSelect = async (formId) => {
    if (!formsPickerData?.tempToken) return;
    try {
      const { data } = await selectGoogleFormsForm(initialActivity.id, formsPickerData.tempToken, formId);
      setFormsPickerData(null);
      const { headers, suggested_mapping } = data;
      if (!headers || headers.length === 0) {
        toast.error('The selected form has no questions. Please choose a different form.');
        return;
      }
      const previousMapping = formsSync?.field_mapping || {};
      const mergedMapping = { ...(suggested_mapping || {}) };
      ['name_column', 'email_column', 'phone_column'].forEach((field) => {
        if (previousMapping[field] && headers.includes(previousMapping[field])) {
          mergedMapping[field] = previousMapping[field];
        }
      });
      setSyncModalData({ tempToken: formsPickerData.tempToken, headers, suggestedMapping: mergedMapping });
      setSyncModalOpen(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not load the selected form.';
      toast.error(msg);
    }
  };

  // Only one of phone/email can be optional — enforced here
  const updateDefaultField = (id, upd) => {
    if (upd.mandatory === false && id !== 'default_name') {
      const otherOptional = defaultFields.find(
        (f) => f.id !== id && f.id !== 'default_name' && !f.mandatory
      );
      if (otherOptional) {
        setRegOptionalMessage('Only one of Phone or Email can be optional at a time.');
        setTimeout(() => setRegOptionalMessage(''), 4000);
        return;
      }
    }
    setRegOptionalMessage('');
    setDefaultFields((p) => p.map((f) => f.id === id ? { ...f, ...upd } : f));
  };

  const moveDefaultField = (id, dir) => setDefaultFields((p) => {
    const nn = p.slice(1); const i = nn.findIndex((f) => f.id === id);
    if (i < 0 || i + dir < 0 || i + dir >= nn.length) return p;
    const n = [...nn]; [n[i], n[i + dir]] = [n[i + dir], n[i]]; return [p[0], ...n];
  });
  const addCustomField    = () => setCustomFields((p) => [...p, makeCustomField()]);
  const removeCustomField = (id) => { setCustomFields((p) => p.filter((f) => f.id !== id)); setFieldErrors((p) => { const s = new Set(p); s.delete(id); return s; }); };
  const updateCustomField = (id, upd) => setCustomFields((p) => p.map((f) => f.id === id ? { ...f, ...upd } : f));
  const changeCFType      = (id, t)   => { setCustomFields((p) => p.map((f) => f.id === id ? { ...f, type: t, options: [] } : f)); setFieldErrors((p) => { const s = new Set(p); s.delete(id); return s; }); };
  const copyCustomField   = (id) => setCustomFields((p) => {
    const idx = p.findIndex((f) => f.id === id); if (idx < 0) return p;
    const src = p[idx];
    const dup = { ...src, id: `cf_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, name: `${src.name} (copy)`, options: src.options.map(o => ({ ...o, id: `opt_${Date.now()}_${Math.random().toString(36).slice(2,5)}` })) };
    const n = [...p]; n.splice(idx + 1, 0, dup); return n;
  });
  const moveCF            = (id, dir) => setCustomFields((p) => {
    const i = p.findIndex((f) => f.id === id);
    if (i < 0 || i + dir < 0 || i + dir >= p.length) return p;
    const n = [...p]; [n[i], n[i + dir]] = [n[i + dir], n[i]]; return n;
  });
  const addOption    = (fid) => setCustomFields((p) => p.map((f) => f.id !== fid || f.options.length >= 10 ? f : { ...f, options: [...f.options, { id: `opt_${Date.now()}`, value: '' }] }));
  const removeOption = (fid, oid) => setCustomFields((p) => p.map((f) => f.id !== fid ? f : { ...f, options: f.options.filter((o) => o.id !== oid) }));
  const updateOption = (fid, oid, v) => setCustomFields((p) => p.map((f) => f.id !== fid ? f : { ...f, options: f.options.map((o) => o.id === oid ? { ...o, value: v } : o) }));

  const getDupIds = () => {
    const defNames = defaultFields.map((f) => f.name.toLowerCase());
    const seen = new Map(); const dup = new Set();
    customFields.forEach((f) => {
      const l = f.name.trim().toLowerCase(); if (!l) return;
      if (defNames.includes(l) || seen.has(l)) dup.add(f.id); else seen.set(l, true);
    });
    return dup;
  };

  // ── Publish ───────────────────────────────────────────────────────────────────
  const handlePublish = async () => {
    // Validate: check for empty custom fields (only when reg form is active)
    const emptyFieldsPub = regRequirement === 'yes' ? customFields.filter(f => {
      if (f.type === 'instruction') return !(f.name || '').replace(/<[^>]+>/g, '').trim();
      return !f.name?.trim();
    }) : [];
    if (emptyFieldsPub.length > 0) {
      toast.warning(`${emptyFieldsPub.length} form field${emptyFieldsPub.length > 1 ? 's are' : ' is'} empty. Add content to all fields before publishing.`);
      return;
    }
    // Validate new update fields (if any are filled, heading is required)
    const hasUpdateContent = newUpdateContent.trim() || newUpdateMedia.length > 0;
    if (hasUpdateContent && !newUpdateHeading.trim()) {
      setNewUpdateError('Please add a heading to publish this update');
      return;
    }

    const _save    = saveFn    || updateChangemakerActivity;
    const _publish = publishFn || publishChangemakerActivity;

    setPublishError('');
    setPublishing(true);
    try {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      // Always save current state first
      await _save(activityId, buildPayload());
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);

      // Create new update if in edit mode with a heading — this path remains as fallback
      if (isEditMode && newUpdateHeading.trim()) {
        const created = await createOrgActivityUpdate(activityId, {
          heading: newUpdateHeading.trim(),
          content: newUpdateContent,
          media_urls: newUpdateMedia,
          media_names: newUpdateMediaNames,
        });
        setActivityUpdates((prev) => [...prev, created.data]);
        setNewUpdateHeading('');
        setNewUpdateContent('');
        setNewUpdateMedia([]);
        setNewUpdateMediaNames([]);
        setShowNewUpdateForm(false);
      }

      if (!isPublished) {
        // First-time publish
        const publishRes = await _publish(activityId);
        setIsPublished(true);
        setPendingPublishUpdateIds(new Set());
        toast.success('Activity published!');
        if (onPublished) onPublished();
        // Show invite modal for unclaimed / new collaborating orgs
        const pending = publishRes?.data?.pending_email_input;
        if (Array.isArray(pending) && pending.length > 0) {
          setPendingInviteCollabs(pending);
          setShowInviteModal(true);
        }
      } else {
        setPendingPublishUpdateIds(new Set());
        toast.success('Changes saved!');
      }

      if (isEditMode) exitEditMode();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Save failed';
      setPublishError(msg);
      toast.error(msg);
    } finally {
      setPublishing(false);
    }
  };


  // ── Update management handlers ────────────────────────────────────────────────
  const handleUpdateMediaUpload = async (files, setFn, setNamesFn, setUploading, currentUrls) => {
    const remaining = 5 - currentUrls.length;
    if (remaining <= 0) return;
    const toUpload = Array.from(files).slice(0, remaining);
    setUploading(true);
    for (const file of toUpload) {
      // Client-side size check before uploading
      const isVideo  = file.type.startsWith('video/');
      const isPDF    = file.type === 'application/pdf';
      const maxBytes = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
      const typeLabel = isVideo ? 'Video' : isPDF ? 'Document' : 'Image';
      const limitLabel = isVideo ? '50 MB' : '5 MB';
      if (file.size > maxBytes) {
        toast.error(`${typeLabel} uploads are limited to ${limitLabel}. This file is ${(file.size / 1024 / 1024).toFixed(1)} MB.`);
        continue;
      }
      try {
        const _upload = uploadFn || uploadActivityMedia;
        const url = await _upload(file);
        const rawName = file.name.replace(/\.[^/.]+$/, ''); // strip extension
        setFn(prev => [...prev, url].slice(0, 5));
        if (setNamesFn) setNamesFn(prev => [...prev, rawName].slice(0, 5));
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Upload failed for one file');
      }
    }
    setUploading(false);
  };

  const startEditUpdate = (upd) => {
    setEditingUpdateId(upd.id);
    setEditUpdateHeading(upd.heading);
    setEditUpdateContent(upd.content || '');
    setEditUpdateMedia(upd.media_urls || []);
    setEditUpdateMediaNames(upd.media_names || []);
  };

  const handleSaveUpdateEdit = async (updateId) => {
    if (!editUpdateHeading.trim()) {
      toast.error('Heading is required');
      return;
    }
    setEditUpdateSaving(true);
    try {
      const res = await updateOrgActivityUpdate(activityId, updateId, {
        heading: editUpdateHeading.trim(),
        content: editUpdateContent,
        media_urls: editUpdateMedia,
        media_names: editUpdateMediaNames,
      });
      setActivityUpdates((prev) => prev.map((u) => u.id === updateId ? res.data : u));
      setPendingPublishUpdateIds((prev) => new Set([...prev, updateId]));
      setEditingUpdateId(null);
      toast.success('Update saved!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setEditUpdateSaving(false);
    }
  };

  const handleDeleteUpdate = async (updateId) => {
    if (!window.confirm('Delete this update? This cannot be undone.')) return;
    try {
      await deleteOrgActivityUpdate(activityId, updateId);
      setActivityUpdates((prev) => prev.filter((u) => u.id !== updateId));
      setPendingPublishUpdateIds((prev) => { const s = new Set(prev); s.delete(updateId); return s; });
      toast.success('Update deleted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const scrollTo = (idx) => {
    [detailsRef, regRef, publishRef][idx]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const isVideoUrl = (url) => /\.(mp4|mov|webm)(\?.*)?$/i.test(url || '');
  const showRegBuilder = regRequirement === 'yes' && regType === 'karmana';
  const dupIds = getDupIds();

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <>
    <div className="relative bg-gray-50">

      {/* ── Fixed vertical progress rail (right side) ─────────────────────── */}
      {/* Vertical line — hidden on mobile, tracks content bounds on desktop */}
      <div
        className="fixed w-px bg-gray-200 z-20 pointer-events-none hidden lg:block"
        style={{ right: lineRight, top: 56, bottom: lineBottom }}
      />

      {/* Save status indicator near top of line — hidden on mobile, hidden when idle */}
      <div
        data-testid="edit-autosave-indicator"
        className="fixed z-30 pointer-events-none hidden lg:block"
        style={{ right: lineRight + 4, top: 62 }}
      >
        {saveStatus !== 'idle' && (
          <span className={`text-[11px] bg-white px-2 py-0.5 rounded-full shadow-sm border border-gray-100 whitespace-nowrap transition-colors ${
            saveStatus === 'saving' ? 'text-gray-400' : 'text-green-600'
          }`}>
            {saveStatus === 'saving' ? 'Saving…' : '✓ Saved'}
          </span>
        )}
      </div>

      {/* Dots on the line — hidden on mobile */}
      {SECTION_LABELS.map((_, idx) => (
        <div
          key={`dot-${idx}`}
          className="fixed z-20 cursor-pointer hidden lg:block"
          style={{
            top: dotPositions[idx],
            right: lineRight,
            transform: 'translate(3px, -50%)',
            transition: 'top 0.25s ease-out',
          }}
          onClick={() => scrollTo(idx)}
          data-testid={`progress-step-${idx}`}
        >
          <div className={`w-2.5 h-2.5 rounded-full border-2 transition-all ${
            activeSection === idx ? 'bg-gray-900 border-gray-900' : 'bg-white border-gray-300'
          }`} />
        </div>
      ))}

      {/* Labels — to the RIGHT of dots on desktop only */}
      {SECTION_LABELS.map((label, idx) => (
        <span
          key={`lbl-${idx}`}
          className="fixed z-20 text-xs whitespace-nowrap select-none cursor-pointer hidden lg:block"
          style={{
            left: labelLeft,
            top: dotPositions[idx],
            transform: 'translateY(-50%)',
            transition: 'top 0.25s ease-out',
          }}
          onClick={() => scrollTo(idx)}
        >
          <span className={activeSection === idx ? 'text-gray-900 font-semibold' : 'text-gray-600'}>
            {label}
          </span>
        </span>
      ))}

      {/* ── Content ────────────────────────────────────────────────────────── */}
      <div ref={contentRef} className="pb-16">

        {/* ══ Section 1: Activity Details ══════════════════════════════════ */}
        <div ref={detailsRef} className="px-4 pt-10 space-y-5 bg-white">

          {/* Heading — label above (with asterisk), auto-resize textarea, border on focus */}
          <div>
            {isEditMode && (
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                Heading <span className="text-red-500">*</span>
              </p>
            )}
            {isEditMode ? (
              <HeadingInput value={name} onChange={setName} />
            ) : (
              <h1 className="text-2xl font-bold text-gray-900 leading-tight">
                {name || <span className="text-gray-300 font-normal italic text-lg">Untitled Activity</span>}
              </h1>
            )}
          </div>

          {/* Areas of Interest */}
          <div>
            <button
              onClick={() => isEditMode && setInterestsOpen((v) => !v)}
              className={`flex items-center w-full text-left ${isEditMode ? 'cursor-pointer' : 'cursor-default'}`}
              data-testid="interests-toggle"
            >
              <SecLabel>Areas of Interest <span className="normal-case font-normal text-gray-400">(choose 1 or 2 most relevant tags — improves visibility)</span></SecLabel>
              {isEditMode && (
                <span className="ml-auto text-gray-400 mb-1.5">{interestsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
              )}
            </button>

            {(!interestsOpen || !isEditMode) && areasOfInterest.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {areasOfInterest.map((id) => {
                  const interest = interests.find((i) => i.id === id);
                  if (!interest) return null;
                  return (
                    <span key={id} className="px-2 py-0.5 bg-white text-gray-600 text-[11px] rounded-full flex items-center gap-1 border border-gray-300">
                      <InterestIcon icon={interest.icon} size={11} className="text-gray-500 flex-shrink-0" />
                      {interest.name}
                    </span>
                  );
                })}
              </div>
            )}

            {isEditMode && interestsOpen && (
              <div className="mt-2 border border-gray-200 rounded-xl overflow-hidden" data-testid="interests-picker">
                <SubcategoryPicker
                  categories={interests}
                  selected={areasOfInterest}
                  onChange={setAreasOfInterest}
                  testidPrefix="ep-interest"
                />
              </div>
            )}

            {!isEditMode && areasOfInterest.length === 0 && (
              <p className="text-xs text-gray-400 italic mt-1">No areas of interest selected.</p>
            )}
          </div>
        </div>

        {/* ── Image/Video carousel ──────────────────────────────────────── */}
        <div className="mt-4 bg-gray-50">
          {isEditMode ? (
            <div className="py-0 space-y-0">
              {mediaUrls.length === 0 ? (
                /* No media yet — upload prompt */
                <label
                  className="flex flex-col items-center justify-center gap-2 w-full py-10 border-2 border-dashed border-gray-300 rounded-none cursor-pointer hover:bg-gray-100 transition-colors bg-gray-50"
                  data-testid="media-upload-area"
                >
                  {isUploading ? (
                    <Loader size={28} className="text-gray-400 animate-spin" />
                  ) : (
                    <>
                      <Upload size={28} className="text-gray-300" />
                      <p className="text-sm font-medium text-gray-500">
                        {isAdmin ? 'Click to upload images, videos or PDF' : 'Click to upload images or videos'}
                      </p>
                      <p className="text-xs text-gray-400">
                        {isAdmin
                          ? 'Up to 5 images/videos · PDF: first 5 pages become carousel images'
                          : 'Up to 5 images/videos · First 5 selected if more chosen'}
                      </p>
                    </>
                  )}
                  <input type="file" accept={isAdmin ? 'image/*,video/*,application/pdf' : 'image/*,video/*'} multiple className="hidden"
                    onChange={(e) => handleMediaUpload(e.target.files)} data-testid="media-upload-0" />
                </label>
              ) : (
                /* Has media — single-item carousel with arrows + dots */
                <div data-testid="edit-carousel">
                  {/* Carousel */}
                  <div className="relative">
                    <div
                      ref={editCarouselRef}
                      className="flex overflow-x-hidden"
                      style={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                      onScroll={(e) => {
                        const idx = Math.round(e.currentTarget.scrollLeft / (e.currentTarget.offsetWidth || 1));
                        setEditCarouselIdx(idx);
                      }}
                    >
                      {mediaUrls.map((url, idx) => (
                        <div
                          key={idx}
                          className="flex-shrink-0 w-full bg-gray-50"
                          style={{ scrollSnapAlign: 'start', aspectRatio: '4 / 5' }}
                        >
                          {isVideoUrl(url)
                            ? <video src={url} className="w-full h-full object-contain" muted playsInline />
                            : <img src={url} alt={`media ${idx + 1}`} className="w-full h-full object-contain" />}
                        </div>
                      ))}
                    </div>

                    {/* Arrow buttons */}
                    {mediaUrls.length > 1 && (
                      <>
                        <button
                          onClick={() => { const ni = Math.max(0, editCarouselIdx - 1); setEditCarouselIdx(ni); carouselScrollTo(ni); }}
                          disabled={editCarouselIdx === 0}
                          data-testid="edit-carousel-prev"
                          className={`absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm rounded-full p-1.5 shadow-md transition-opacity ${editCarouselIdx === 0 ? 'opacity-30' : 'opacity-90 hover:opacity-100'}`}
                        >
                          <ChevronLeft size={16} className="text-gray-700" />
                        </button>
                        <button
                          onClick={() => { const ni = Math.min(mediaUrls.length - 1, editCarouselIdx + 1); setEditCarouselIdx(ni); carouselScrollTo(ni); }}
                          disabled={editCarouselIdx === mediaUrls.length - 1}
                          data-testid="edit-carousel-next"
                          className={`absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm rounded-full p-1.5 shadow-md transition-opacity ${editCarouselIdx === mediaUrls.length - 1 ? 'opacity-30' : 'opacity-90 hover:opacity-100'}`}
                        >
                          <ChevronRight size={16} className="text-gray-700" />
                        </button>
                      </>
                    )}
                  </div>

                  {/* Dot indicators */}
                  {mediaUrls.length > 1 && (
                    <div className="flex justify-center gap-1.5 py-2 bg-gray-50" data-testid="edit-carousel-dots">
                      {mediaUrls.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => { setEditCarouselIdx(i); carouselScrollTo(i); }}
                          className={`rounded-full transition-all ${i === editCarouselIdx ? 'w-4 h-1.5 bg-gray-700' : 'w-1.5 h-1.5 bg-gray-300'}`}
                        />
                      ))}
                    </div>
                  )}

                  {/* Controls row */}
                  <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50">
                    <p className="text-xs text-gray-400">
                      {editCarouselIdx + 1} / {mediaUrls.length} &nbsp;·&nbsp; {mediaUrls.length}/5 uploaded
                    </p>
                    <div className="flex gap-3 items-center">
                      {mediaUrls.length < 5 && !isUploading && (
                        <label className="text-xs text-green-700 font-semibold cursor-pointer hover:underline">
                          + Add more
                          <input type="file" accept={isAdmin ? 'image/*,video/*,application/pdf' : 'image/*,video/*'} multiple className="hidden"
                            onChange={(e) => handleMediaUpload(e.target.files)} data-testid="media-add-more" />
                        </label>
                      )}
                      {isUploading && <Loader size={14} className="animate-spin text-gray-400" />}
                      <button
                        onClick={removeAllMedia}
                        data-testid="media-remove-all"
                        className="flex items-center gap-1 text-xs text-red-500 font-semibold hover:text-red-700 transition-colors"
                      >
                        <X size={12} /> Remove All
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* View mode carousel — same style as public page */
            mediaUrls.length > 0 ? (
              <div className="flex overflow-x-auto" style={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none' }}>
                {mediaUrls.map((url, idx) => (
                  <div key={idx} className="flex-shrink-0 w-full" style={{ scrollSnapAlign: 'start', aspectRatio: '4 / 5' }}>
                    {isVideoUrl(url)
                      ? <video src={url} className="w-full h-full object-contain bg-gray-900" controls />
                      : <img src={url} alt={`media ${idx + 1}`} className="w-full h-full object-contain bg-gray-50" />}
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-32 flex items-center justify-center text-gray-300 text-xs">No media uploaded</div>
            )
          )}
        </div>

        {/* ── Continue Activity Details ───────────────────────────────────── */}
        <div className="px-4 pt-5 space-y-5 bg-white">

          {/* Organised By + Collaborators */}
          {organizer && (
            <div>
              <div className="flex items-center justify-between">
                <SecLabel>Organised by</SecLabel>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-full border border-gray-200 w-fit">
                {organizer.logo_url
                  ? <img src={organizer.logo_url} alt={organizer.name} className="w-5 h-5 rounded-full object-contain bg-white" />
                  : <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center text-[9px] font-bold text-green-700">{organizer.name?.[0]}</div>}
                <span className="text-sm font-medium text-gray-700">{organizer.name}</span>
              </div>
              {activityId && (
                <ActivityCollaboratorSection
                  activityId={activityId}
                  isMainOrg={isMainOrg}
                  isAdmin={isAdmin}
                  readOnly={!isEditMode}
                  onNeedsEmailInput={(pending) => {
                    if (pending?.length) {
                      setPendingInviteCollabs(pending);
                      setShowInviteModal(true);
                    }
                  }}
                />
              )}
            </div>
          )}

          {/* Description — border disappears when has content */}
          <div>
            <SecLabel>Description</SecLabel>
            {isEditMode ? (
              <div className={`rounded-xl overflow-hidden transition-all duration-150 ${description ? '' : 'border border-gray-200'}`}>
                <RichTextEditor
                  value={description}
                  onChange={setDescription}
                  placeholder="Describe the activity, what participants can expect…"
                  showLink={false}
                  noBorder={true}
                  minHeight={120}
                />
              </div>
            ) : description ? (
              <SafeHtml html={description} className="text-gray-700 text-sm leading-relaxed prose prose-sm max-w-none" />
            ) : (
              <p className="text-gray-400 text-sm italic">No description yet.</p>
            )}
          </div>

          {/* Happening On */}
          <div data-testid="edit-happening-on">
            <SecLabel>Happening On</SecLabel>
            {isEditMode ? (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <FloatDateField
                      label="Date"
                      value={date}
                      onChange={setDate}
                      type="date"
                      required
                      data-testid="edit-date"
                    />
                  </div>
                  <div className="flex-1">
                    <FloatDateField
                      label="End Date (if Multiple Days)"
                      value={dateEnd}
                      onChange={setDateEnd}
                      type="date"
                      min={date ? addDay(date) : undefined}
                      data-testid="edit-date-end"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <AmPmTimePicker
                      label="Start Time"
                      value={startTime}
                      onChange={setStartTime}
                    />
                  </div>
                  <div className="flex-1">
                    <AmPmTimePicker
                      label="End Time"
                      value={endTime}
                      onChange={setEndTime}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2.5">
                <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock size={14} className="text-blue-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {date ? formatDateRange(date, dateEnd || null) : <span className="text-gray-400 italic">No date set</span>}
                  </p>
                  {(startTime || endTime) && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      {formatTime12h(startTime)}{endTime ? ` – ${formatTime12h(endTime)}` : ''}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Happening At */}
          <div data-testid="edit-happening-at">
            <SecLabel>Happening At</SecLabel>
            {isEditMode ? (
              <div className="space-y-2">
                {/* Physical / Online toggle */}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => { setLocIsOnline(false); if (locAddress === 'Online') setLocAddress(''); }}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${!locIsOnline ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'}`}
                    data-testid="toggle-physical"
                  >
                    Physical
                  </button>
                  <button
                    type="button"
                    onClick={() => { setLocIsOnline(true); setLocAddress('Online'); }}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${locIsOnline ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'}`}
                    data-testid="toggle-online"
                  >
                    <Globe size={13} className="inline mr-1.5 -mt-0.5" />Online
                  </button>
                </div>
                <FloatField
                  label="Address"
                  value={locIsOnline ? 'Online' : locAddress}
                  onChange={locIsOnline ? undefined : setLocAddress}
                  disabled={locIsOnline}
                  required={!locIsOnline}
                  data-testid="edit-address"
                />
                <FloatField
                  label={locIsOnline ? 'Meeting Link' : 'Google Maps Link'}
                  value={locMapLink}
                  onChange={setLocMapLink}
                  data-testid="edit-map-link"
                />
                <FloatField
                  label="How to Reach (directions for participants)"
                  value={howToReach}
                  onChange={setHowToReach}
                  multiline
                  rows={2}
                  data-testid="edit-how-to-reach"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-start gap-2.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${locIsOnline ? 'bg-teal-50' : 'bg-green-50'}`}>
                    {locIsOnline
                      ? <Globe size={14} className="text-teal-500" />
                      : <MapPin size={14} className="text-green-500" />
                    }
                  </div>
                  <p className="text-sm text-gray-700 leading-snug">
                    {locIsOnline ? 'Online' : (locAddress || <span className="text-gray-400 italic">No location set</span>)}
                  </p>
                </div>
                {howToReach && (
                  <p className="text-sm text-gray-500 pl-10 leading-snug">{howToReach}</p>
                )}
              </div>
            )}
          </div>

          {/* Things to Bring */}
          <div>
            <SecLabel>Things to Bring</SecLabel>
            {isEditMode ? (
              <FloatField label="Things to Bring" value={thingsToBring} onChange={setThingsToBring} multiline rows={2} data-testid="edit-things" />
            ) : thingsToBring ? (
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{thingsToBring}</p>
            ) : (
              <p className="text-gray-400 text-sm italic">Nothing specified.</p>
            )}
          </div>
        </div>

        {/* ══ Section 2: Registration Form (yellow bg) ══════════════════════ */}
        <div ref={regRef} data-testid="edit-reg-section" className="px-4 py-6 bg-yellow-50">
          <h2 className="text-base font-bold text-gray-800 mb-4">Registration Form</h2>

          {isEditMode ? (
            /* ── EDIT MODE: full interactive form builder ── */
            <>
              <div>
                <p className="text-sm font-semibold text-gray-700">Is Registration Required?</p>
                <ThreeOptionPicker value={regRequirement} onChange={setRegRequirement} />
              </div>

              {regRequirement === 'yes' && (
                <div className="space-y-3 mt-4">

                  {/* Karmana Registration (RECOMMENDED) — first */}
                  <label className={`flex items-start gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all ${regType === 'karmana' ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-testid="reg-type-karmana">
                    <input type="radio" name="reg-type-ep" value="karmana" checked={regType === 'karmana'} onChange={() => handleRegTypeChange('karmana')} className="mt-0.5 accent-green-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">
                        Karmana Registration <span className="text-green-600 text-xs font-bold">(RECOMMENDED)</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">Build a custom registration form for participants</p>
                    </div>
                  </label>

                  {/* Other Registration Link — second */}
                  <label className={`flex items-start gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all ${regType === 'link' ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-testid="reg-type-link">
                    <input type="radio" name="reg-type-ep" value="link" checked={regType === 'link'} onChange={() => handleRegTypeChange('link')} className="mt-0.5 accent-green-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-800">Other Registration Link</p>
                      <p className="text-xs text-gray-400 mt-0.5">Redirect participants to an external form or page</p>
                      {regType === 'link' && (
                        <>
                          <input
                            className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                            placeholder="https://…" value={regLink} onChange={(e) => setRegLink(e.target.value)}
                            data-testid="reg-link-input" />
                          {initialActivity.id && (
                            <FormsSyncBlock formsSync={formsSync} onSync={handleSyncResponses} onReSync={handleReSyncResponses} onTurnOff={handleTurnOffSync} disabled={!isEditMode} />
                          )}
                          {/* Embedded form preview — shown after sync is confirmed with a valid /d/e/ URL */}
                          {formsSync?.sync_status === 'active' && (() => {
                            const url = formsSync.respondent_url || '';
                            // Only /d/e/ published respondent URLs can be embedded; edit URLs return 401
                            if (!url || !url.includes('/d/e/')) return null;
                            const embedUrl = `${url.split('?')[0].split('#')[0]}?embedded=true`;
                            return (
                              <div className="mt-4" data-testid="edit-form-preview">
                                <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Form Preview</p>
                                <GoogleFormEmbed embedUrl={embedUrl} isPreview={true} />
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  </label>

                  {/* Karmana form builder */}
                  {showRegBuilder && (
                    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white" data-testid="karmana-form-builder-ep">

                      {/* ── Default fields (Name, Phone, Email) ── */}
                      <div className="divide-y divide-gray-100">
                        {defaultFields.map((field) => {
                          const isName  = field.id === 'default_name';
                          const nonName = defaultFields.slice(1);
                          const nIdx    = isName ? -1 : nonName.findIndex((f) => f.id === field.id);
                          const typeLabel = DEFAULT_TYPE_LABEL[field.type] || field.type;
                          return (
                            <div key={field.id} className="px-3 pt-2.5 pb-2">
                              {/* Row 1: Type (locked) + Question */}
                              <div className="flex items-center gap-2">
                                {/* Type cell – locked label with icon */}
                                <div className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-gray-100 border border-gray-200 flex-shrink-0" style={{ minWidth: 90 }}>
                                  <FieldTypeIcon type={field.type} size={11} />
                                  <span className="text-[11px] text-gray-500 font-medium whitespace-nowrap">{typeLabel}</span>
                                </div>
                                {/* Question input */}
                                <input
                                  className="flex-1 min-w-0 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-green-400"
                                  value={field.name}
                                  onChange={(e) => updateDefaultField(field.id, { name: e.target.value })}
                                  placeholder="Question"
                                />
                              </div>
                              {/* Row 2: Required toggle + arrows (no duplicate/delete for defaults) */}
                              <div className="flex items-center gap-2.5 mt-2">
                                <span className="text-[11px] text-gray-500 font-medium">Required</span>
                                <Toggle
                                  checked={field.mandatory}
                                  onChange={(v) => updateDefaultField(field.id, { mandatory: v })}
                                  disabled={isName}
                                />
                                <span className="flex-1" />
                                {!isName && (
                                  <>
                                    <button onClick={() => moveDefaultField(field.id, -1)} disabled={nIdx === 0} className={`p-1 rounded ${nIdx === 0 ? 'text-gray-200' : 'text-gray-400 hover:bg-gray-100'}`} title="Move up"><ArrowUp size={12} /></button>
                                    <button onClick={() => moveDefaultField(field.id, 1)} disabled={nIdx === nonName.length - 1} className={`p-1 rounded ${nIdx === nonName.length - 1 ? 'text-gray-200' : 'text-gray-400 hover:bg-gray-100'}`} title="Move down"><ArrowDown size={12} /></button>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Only-one-optional constraint message */}
                      {regOptionalMessage && (
                        <div className="px-3 pb-2" data-testid="reg-optional-warning">
                          <p className="text-[11px] text-amber-600 font-medium">{regOptionalMessage}</p>
                        </div>
                      )}

                      {/* ── Custom fields ── */}
                      {customFields.length > 0 && (
                        <div className="border-t border-gray-200 divide-y divide-gray-100">
                          {customFields.map((field, idx) => {
                            const isDup  = dupIds.has(field.id);
                            const hasErr = fieldErrors.has(field.id);
                            const cfType = CUSTOM_FIELD_TYPES.find(t => t.value === field.type) || CUSTOM_FIELD_TYPES[0];
                            return (
                              <div key={field.id} className="px-3 pt-2.5 pb-2">
                                {/* Row 1: Type dropdown + Question input */}
                                <div className={`flex ${field.type === 'instruction' ? 'items-start' : 'items-center'} gap-2`}>
                                  {/* Custom type dropdown with icons */}
                                  <FieldTypeDropdown
                                    value={field.type}
                                    onChange={(t) => changeCFType(field.id, t)}
                                  />
                                  {/* Question input — hidden for instruction type */}
                                  {field.type !== 'instruction' && (
                                    <input
                                      className={`flex-1 min-w-0 px-2.5 py-1.5 rounded-lg border text-xs focus:outline-none focus:ring-1 focus:ring-green-400 text-gray-700 ${isDup ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}
                                      placeholder="Question"
                                      value={field.name}
                                      onChange={(e) => updateCustomField(field.id, { name: e.target.value })}
                                    />
                                  )}
                                </div>

                                {/* Instruction rich text editor */}
                                {field.type === 'instruction' && (
                                  <>
                                    <div className="mt-2 rounded-xl overflow-hidden border border-gray-200">
                                      <RichTextEditor
                                        value={field.name}
                                        onChange={(v) => updateCustomField(field.id, { name: v })}
                                        placeholder="Write instruction text for participants…"
                                        showLink={false}
                                        noBorder={true}
                                        minHeight={60}
                                      />
                                    </div>
                                    {/* Instruction image upload */}
                                    <div className="mt-2">
                                      {field.instruction_image_url ? (
                                        <div className="relative inline-block">
                                          <img
                                            src={field.instruction_image_url}
                                            alt="Instruction"
                                            className="max-h-40 rounded-lg border border-gray-200 object-contain"
                                          />
                                          <button
                                            type="button"
                                            onClick={() => updateCustomField(field.id, { instruction_image_url: null })}
                                            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                                          >
                                            <X size={10} />
                                          </button>
                                        </div>
                                      ) : (
                                        <label className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-green-600 cursor-pointer w-fit">
                                          <input
                                            type="file"
                                            accept="image/jpeg,image/jpg,image/png,image/webp"
                                            className="hidden"
                                            onChange={async (e) => {
                                              const f = e.target.files?.[0];
                                              if (!f) return;
                                              if (f.size > 10 * 1024 * 1024) { toast.error('Image must be under 10 MB'); return; }
                                              try {
                                                const res = await uploadUserImage(f);
                                                updateCustomField(field.id, { instruction_image_url: res.data.url });
                                              } catch { toast.error('Upload failed. Please try again.'); }
                                              e.target.value = '';
                                            }}
                                          />
                                          <ImageIcon size={12} />
                                          Add image (optional, max 10 MB)
                                        </label>
                                      )}
                                    </div>
                                  </>
                                )}

                                {/* Row 2: Required toggle (hidden for instruction) + actions */}
                                <div className="flex items-center gap-2.5 mt-2">
                                  {field.type !== 'instruction' && (
                                    <>
                                      <span className="text-[11px] text-gray-500 font-medium">Required</span>
                                      <Toggle
                                        checked={field.mandatory}
                                        onChange={(v) => updateCustomField(field.id, { mandatory: v })}
                                      />
                                    </>
                                  )}
                                  <span className="flex-1" />
                                  <button onClick={() => copyCustomField(field.id)} className="p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600" title="Duplicate field"><Copy size={12} /></button>
                                  <button onClick={() => removeCustomField(field.id)} className="p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50" title="Delete field"><Trash2 size={12} /></button>
                                  <button onClick={() => moveCF(field.id, -1)} disabled={idx === 0} className={`p-1 rounded ${idx === 0 ? 'text-gray-200' : 'text-gray-400 hover:bg-gray-100'}`} title="Move up"><ArrowUp size={12} /></button>
                                  <button onClick={() => moveCF(field.id, 1)} disabled={idx === customFields.length - 1} className={`p-1 rounded ${idx === customFields.length - 1 ? 'text-gray-200' : 'text-gray-400 hover:bg-gray-100'}`} title="Move down"><ArrowDown size={12} /></button>
                                </div>

                                {isDup && <p className="text-[11px] text-amber-600 font-medium mt-1">Duplicate field name</p>}

                                {/* Options (for Multiple Choice, Checkbox, Dropdown) */}
                                {HAS_OPTIONS(field.type) && (
                                  <div className="mt-2 pl-1 space-y-1.5">
                                    <p className="text-[11px] text-gray-400 font-medium">Options ({field.options.filter((o) => o.value.trim()).length}/10)</p>
                                    {field.options.map((opt) => (
                                      <div key={opt.id} className="flex items-center gap-1.5">
                                        <input
                                          className="flex-1 min-w-0 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-1 focus:ring-green-400"
                                          placeholder="Option value…"
                                          value={opt.value}
                                          onChange={(e) => updateOption(field.id, opt.id, e.target.value)}
                                        />
                                        <button onClick={() => removeOption(field.id, opt.id)} className="p-1 text-gray-300 hover:text-red-500 flex-shrink-0"><X size={11} /></button>
                                      </div>
                                    ))}
                                    {field.options.length < 10 && (
                                      <button onClick={() => addOption(field.id)} className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 font-medium mt-0.5">
                                        <Plus size={11} /> Add Option
                                      </button>
                                    )}
                                    {hasErr && <p className="text-[11px] text-red-500 font-medium">Add at least one option to proceed</p>}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div className="px-3 py-2.5 border-t border-gray-100 bg-white">
                        <button onClick={addCustomField} className="flex items-center gap-1.5 text-sm text-green-700 hover:text-green-800 font-semibold" data-testid="ep-add-custom-field">
                          <Plus size={14} /> Add Field
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Registration End Date ── */}
              {regRequirement === 'yes' && <div className="mt-3 pt-3 border-t border-gray-100" data-testid="ep-reg-end-date-section">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Registration End Date</p>
                <input
                  type="date"
                  value={regEndDate}
                  max={dateEnd || date || undefined}
                  onChange={(e) => { setRegEndDate(e.target.value); setRegEndDateModified(true); }}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-green-400"
                  data-testid="ep-reg-end-date-input"
                />
                {!regEndDateModified && regEndDate && (
                  <p className="text-[11px] text-gray-400 mt-1">
                    Same as activity {dateEnd ? 'end' : ''} date
                  </p>
                )}
              </div>}

            </>
          ) : (
            /* ── VIEW MODE: read-only registration summary ── */
            <div className="space-y-3" data-testid="reg-section-readonly">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 w-40 flex-shrink-0">Registration Required</span>
                <span className={`text-sm font-semibold capitalize ${regRequirement === 'no' ? 'text-gray-500' : 'text-gray-800'}`} data-testid="reg-requirement-display">
                  {regRequirement === 'yes' ? 'Yes' : 'No'}
                </span>
              </div>

              {regRequirement === 'yes' && (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 w-40 flex-shrink-0">Registration Type</span>
                    <span className="text-sm font-semibold text-gray-800" data-testid="reg-type-display">
                      {regType === 'karmana' ? 'Karmana Registration' : regType === 'link' ? 'Other Registration Link' : '—'}
                    </span>
                  </div>

                  {regType === 'link' && regLink && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm text-gray-500 w-40 flex-shrink-0">Registration URL</span>
                      <a href={regLink} target="_blank" rel="noopener noreferrer" className="text-sm text-green-600 underline break-all">{regLink}</a>
                    </div>
                  )}

                  {regType === 'karmana' && (
                    <div className="mt-1">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Form Preview</p>
                      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white divide-y divide-gray-100" data-testid="reg-fields-readonly">
                        {[...defaultFields, ...customFields].map((field) => (
                          <div key={field.id} className="px-3 py-2.5">
                            {field.type === 'instruction' ? (
                              /* Instruction: render HTML content + optional image */
                              <div>
                                <SafeHtml
                                  html={field.name || '<p class="text-gray-400 italic text-xs">Empty instruction</p>'}
                                  className="text-xs text-gray-700 prose prose-xs max-w-none leading-relaxed"
                                />
                                {field.instruction_image_url && (
                                  <div className="mt-2 flex justify-center">
                                    <img src={field.instruction_image_url} alt="Instruction" className="max-h-48 rounded-lg border border-gray-200 object-contain" />
                                  </div>
                                )}
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center gap-1.5 mb-1.5">
                                  <span className="text-xs font-semibold text-gray-700">
                                    {field.name || <span className="italic text-gray-400">Unnamed field</span>}
                                  </span>
                                  {field.mandatory && <span className="text-red-400 text-xs">*</span>}
                                </div>
                                <DisabledInput type={field.type} options={field.options} placeholder={field.placeholder} />
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {regEndDate && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 w-40 flex-shrink-0">Registration Ends</span>
                      <span className="text-sm font-semibold text-gray-800" data-testid="reg-end-date-display">
                        {new Date(`${regEndDate}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                  )}

                  {/* Google Forms Sync — view mode */}
                  {regType === 'link' && initialActivity.id && (
                    <div className="pt-2 border-t border-gray-100" data-testid="ep-forms-sync-view">
                      <FormsSyncBlock
                        formsSync={formsSync}
                        onSync={handleSyncResponses}
                        onReSync={handleReSyncResponses}
                        onTurnOff={handleTurnOffSync}
                      />
                      {/* Embedded form preview — shown after sync is confirmed with a valid /d/e/ URL */}
                      {formsSync?.sync_status === 'active' && (() => {
                        const url = formsSync.respondent_url || '';
                        // Only /d/e/ published respondent URLs can be embedded; edit URLs return 401
                        if (!url || !url.includes('/d/e/')) return null;
                        const embedUrl = `${url.split('?')[0].split('#')[0]}?embedded=true`;
                        return (
                          <div className="mt-4" data-testid="edit-form-preview-view">
                            <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Form Preview</p>
                            <GoogleFormEmbed embedUrl={embedUrl} isPreview={true} />
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* ══ Section 2c: Participant Limit ══════════════════════════════════ */}
        <div className="mt-0 px-4 py-5 border-t border-gray-100" data-testid="ep-participant-limit-section">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-base font-bold text-gray-800">Participant Limit</h2>
          </div>

          {/* Is Number of Participants Fixed? */}
          <div className="mb-4">
            <p className="text-sm font-semibold text-gray-700 mb-2">
              Is Number of Participants Fixed?
            </p>
            {isEditMode ? (
              <div className="flex gap-2">
                {[{ v: false, l: 'No' }, { v: true, l: 'Yes' }].map(({ v, l }) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => setIsParticipantsFixed(v)}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                      isParticipantsFixed === v
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                    data-testid={`ep-participants-fixed-${l.toLowerCase()}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-700">{isParticipantsFixed ? 'Yes' : 'No'}</p>
            )}
          </div>

          {/* Max participants number field — only when fixed=Yes */}
          {isParticipantsFixed && (
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-1.5">
                Maximum number of participants allowed
              </label>
              {isEditMode ? (
                <input
                  type="number"
                  min={1}
                  value={maxParticipants}
                  onChange={(e) => setMaxParticipants(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  className="w-32 px-3 py-1.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-300"
                  data-testid="ep-max-participants-input"
                />
              ) : (
                <p className="text-sm text-gray-700 font-semibold">{maxParticipants}</p>
              )}
            </div>
          )}
        </div>

        {/* ══ Section 2b: Activity Updates (only when published) ══════════ */}
        {isPublished && (
          <div className="mt-6 px-4 py-6 bg-green-50" data-testid="ep-updates-section">
            <div className="flex items-center gap-2 mb-1">
              <Rss size={14} className="text-green-600" />
              <h2 className="text-base font-bold text-gray-800">Activity Updates</h2>
            </div>
            {isEditMode && (
              <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                Create Updates to Notify Participants of any change, send Reminders or share Activity Outcomes.
              </p>
            )}

            {/* Loading */}
            {updatesLoading && <p className="text-xs text-gray-400">Loading updates…</p>}

            {/* Existing updates list */}
            {activityUpdates.length > 0 && (
              <div className="space-y-3 mb-4">
                {activityUpdates.map((upd) => (
                  <div key={upd.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`update-item-${upd.id}`}>
                    {isEditMode && editingUpdateId === upd.id ? (
                      /* ── Inline edit form ── */
                      <div className="p-3 space-y-3">
                        <div>
                          <SecLabel>Heading <span className="text-red-500">*</span></SecLabel>
                          <input
                            value={editUpdateHeading}
                            onChange={(e) => setEditUpdateHeading(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-green-400"
                            placeholder="Update heading…"
                            data-testid={`edit-update-heading-${upd.id}`}
                          />
                        </div>
                        <div>
                          <SecLabel>Media (up to 5 — images, videos or PDFs)</SecLabel>
                          <div className="flex flex-wrap gap-2">
                            {editUpdateMedia.map((url, i) => (
                              <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                                {url.toLowerCase().endsWith('.pdf') ? (
                                  <div className="w-full h-full flex flex-col items-center justify-center bg-red-50">
                                    <FileText size={20} className="text-red-400" />
                                    <span className="text-[9px] text-red-400 font-semibold mt-0.5">PDF</span>
                                  </div>
                                ) : (
                                  <img src={url} alt="" className="w-full h-full object-cover" />
                                )}
                                <button onClick={() => {
                                  setEditUpdateMedia(p => p.filter((_, ii) => ii !== i));
                                  setEditUpdateMediaNames(p => p.filter((_, ii) => ii !== i));
                                }} className="absolute top-0 right-0 bg-black/50 text-white rounded-bl-lg p-0.5">
                                  <X size={10} />
                                </button>
                              </div>
                            ))}
                            {editUpdateMedia.length < 5 && (
                              <label className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:bg-gray-50">
                                {isUploadingEditMedia ? <Loader size={14} className="animate-spin text-gray-400" /> : <Plus size={14} className="text-gray-400" />}
                                <input type="file" accept="image/*,video/*,application/pdf" multiple className="hidden"
                                  onChange={(e) => handleUpdateMediaUpload(e.target.files, setEditUpdateMedia, setEditUpdateMediaNames, setIsUploadingEditMedia, editUpdateMedia)} />
                              </label>
                            )}
                          </div>
                          {editUpdateMedia.length > 0 && (
                            <div className="mt-2 space-y-1.5">
                              {editUpdateMedia.map((url, i) => {
                                const low = url.toLowerCase();
                                const typeLabel = low.endsWith('.pdf') ? 'Document' : /\.(mp4|mov|avi|wmv|mpg)$/.test(low) ? 'Video' : 'Image';
                                return (
                                  <div key={i} className="flex items-center gap-2">
                                    <span className="text-[10px] font-semibold text-gray-500 w-16 flex-shrink-0">{typeLabel}</span>
                                    <input
                                      type="text"
                                      value={editUpdateMediaNames[i] ?? ''}
                                      onChange={e => setEditUpdateMediaNames(p => { const n = [...p]; n[i] = e.target.value; return n; })}
                                      placeholder="File name"
                                      className="flex-1 text-xs border border-gray-200 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-green-400"
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div>
                          <SecLabel>Content</SecLabel>
                          <div className="rounded-xl overflow-hidden border border-gray-200">
                            <RichTextEditor
                              value={editUpdateContent}
                              onChange={setEditUpdateContent}
                              placeholder="Update details…"
                              showLink={false}
                              noBorder={true}
                              minHeight={80}
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveUpdateEdit(upd.id)}
                            disabled={editUpdateSaving}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-900 text-white text-xs font-semibold hover:bg-gray-800 disabled:opacity-50"
                            data-testid={`save-update-${upd.id}`}
                          >
                            {editUpdateSaving ? <Loader size={12} className="animate-spin" /> : null}
                            Save
                          </button>
                          <button onClick={() => setEditingUpdateId(null)}
                            className="px-3 py-2 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50">
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* ── Read-only view ── */
                      <div className="p-3">
                        <p className="text-[10px] font-semibold text-green-600 uppercase tracking-wide">
                          {new Date(upd.published_at || upd.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                        <p className="text-sm font-bold text-gray-800 mt-0.5">{upd.heading}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          {/* Edit & Delete — only in edit mode */}
                          {isEditMode && (
                            <>
                              <button onClick={() => startEditUpdate(upd)}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                data-testid={`edit-update-btn-${upd.id}`}>
                                <Pencil size={11} /> Edit
                              </button>
                              <button onClick={() => handleDeleteUpdate(upd.id)}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-red-100 text-xs font-medium text-red-500 hover:bg-red-50"
                                data-testid={`delete-update-btn-${upd.id}`}>
                                <Trash2 size={11} /> Delete
                              </button>
                            </>
                          )}
                          {/* Send Email — always visible; disabled until Publish happens after save */}
                          <button
                            onClick={() => !pendingPublishUpdateIds.has(upd.id) && setEmailModalUpdate(upd)}
                            disabled={pendingPublishUpdateIds.has(upd.id)}
                            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                              pendingPublishUpdateIds.has(upd.id)
                                ? 'border-gray-100 text-gray-300 bg-gray-50 cursor-not-allowed'
                                : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                            }`}
                            data-testid={`send-email-btn-${upd.id}`}
                          >
                            <Mail size={11} />
                            {upd.email_sent_at ? 'Send Email Again' : 'Send Email'}
                          </button>
                          {upd.email_sent_at && !pendingPublishUpdateIds.has(upd.id) && (
                            <span className="text-[10px] text-gray-400">
                              Last sent {new Date(upd.email_sent_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                          )}
                          {pendingPublishUpdateIds.has(upd.id) && (
                            <span className="text-[10px] text-amber-500 font-medium">Publish to enable email</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* New update form toggle — only in edit mode */}
            {isEditMode && (!showNewUpdateForm ? (
              <button
                onClick={() => { setShowNewUpdateForm(true); setNewUpdateError(''); }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-green-300 text-sm font-semibold text-green-700 hover:bg-green-100 transition-colors w-full justify-center"
                data-testid="ep-new-update-btn"
              >
                <Plus size={14} /> Create New Update
              </button>
            ) : (
              <div className="bg-white rounded-xl border border-green-200 p-3 space-y-3" data-testid="ep-new-update-form">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">New Update</p>
                  <button onClick={() => {
                    setShowNewUpdateForm(false);
                    setNewUpdateHeading('');
                    setNewUpdateContent('');
                    setNewUpdateMedia([]);
                    setNewUpdateError('');
                  }} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
                </div>
                {newUpdateError && (
                  <p className="text-xs text-red-500 font-medium" data-testid="ep-new-update-error">{newUpdateError}</p>
                )}
                <div>
                  <SecLabel>Heading <span className="text-red-500">*</span></SecLabel>
                  <input
                    value={newUpdateHeading}
                    onChange={(e) => { setNewUpdateHeading(e.target.value); setNewUpdateError(''); }}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-green-400"
                    placeholder="What's the update about?"
                    data-testid="ep-new-update-heading"
                  />
                </div>
                <div>
                  <SecLabel>Media (up to 5 — images, videos or PDFs)</SecLabel>
                  <div className="flex flex-wrap gap-2">
                    {newUpdateMedia.map((url, i) => (
                      <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                        {url.toLowerCase().endsWith('.pdf') ? (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-red-50">
                            <FileText size={20} className="text-red-400" />
                            <span className="text-[9px] text-red-400 font-semibold mt-0.5">PDF</span>
                          </div>
                        ) : (
                          <img src={url} alt="" className="w-full h-full object-cover" />
                        )}
                        <button onClick={() => {
                          setNewUpdateMedia(p => p.filter((_, ii) => ii !== i));
                          setNewUpdateMediaNames(p => p.filter((_, ii) => ii !== i));
                        }} className="absolute top-0 right-0 bg-black/50 text-white rounded-bl-lg p-0.5">
                          <X size={10} />
                        </button>
                      </div>
                    ))}
                    {newUpdateMedia.length < 5 && (
                      <label className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:bg-gray-50">
                        {isUploadingUpdateMedia ? <Loader size={14} className="animate-spin text-gray-400" /> : <Plus size={14} className="text-gray-400" />}
                        <input type="file" accept="image/*,video/*,application/pdf" multiple className="hidden"
                          onChange={(e) => handleUpdateMediaUpload(e.target.files, setNewUpdateMedia, setNewUpdateMediaNames, setIsUploadingUpdateMedia, newUpdateMedia)} />
                      </label>
                    )}
                  </div>
                  {newUpdateMedia.length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {newUpdateMedia.map((url, i) => {
                        const low = url.toLowerCase();
                        const typeLabel = low.endsWith('.pdf') ? 'Document' : /\.(mp4|mov|avi|wmv|mpg)$/.test(low) ? 'Video' : 'Image';
                        return (
                          <div key={i} className="flex items-center gap-2">
                            <span className="text-[10px] font-semibold text-gray-500 w-16 flex-shrink-0">{typeLabel}</span>
                            <input
                              type="text"
                              value={newUpdateMediaNames[i] ?? ''}
                              onChange={e => setNewUpdateMediaNames(p => { const n = [...p]; n[i] = e.target.value; return n; })}
                              placeholder="File name"
                              className="flex-1 text-xs border border-gray-200 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-green-400"
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div>
                  <SecLabel>Content</SecLabel>
                  <div className="rounded-xl overflow-hidden border border-gray-200">
                    <RichTextEditor
                      value={newUpdateContent}
                      onChange={setNewUpdateContent}
                      placeholder="Share the update details with participants…"
                      showLink={false}
                      noBorder={true}
                      minHeight={80}
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <p className="text-[11px] text-gray-400">Fill in the heading above, then click <strong>Save</strong> to save this update.</p>
                </div>
              </div>
            ))}

            {/* Empty state in frozen view */}
            {!isEditMode && activityUpdates.length === 0 && (
              <p className="text-xs text-gray-400">No updates posted yet.</p>
            )}
          </div>
        )}

        {/* ══ Section 3: Contact for this Activity ══════════════════════════ */}
        <div className="px-4 py-6 bg-white border-t border-gray-100" data-testid="edit-activity-contacts">
          <h2 className="text-base font-bold text-gray-800 mb-1">Contact for this Activity</h2>
          <p className="text-xs text-gray-400 mb-4">Add Event Day Contacts for participants</p>

          {isEditMode ? (
            <div className="space-y-3">
              {activityContacts.map((c, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`edit-contact-row-${idx}`}>
                  {/* Phone first (left) */}
                  <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden flex-shrink-0">
                    <span className="px-2 py-2 text-sm text-gray-500 bg-gray-50 border-r border-gray-200 font-medium">+91</span>
                    <input
                      value={c.phone}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        const updated = activityContacts.map((ct, i) => i === idx ? { ...ct, phone: val } : ct);
                        setActivityContacts(updated);
                      }}
                      placeholder="Phone no"
                      maxLength={10}
                      inputMode="numeric"
                      className="w-28 px-3 py-2 text-sm focus:outline-none"
                      data-testid={`edit-contact-phone-${idx}`}
                    />
                  </div>
                  {/* Name second (right), reduced width */}
                  <input
                    value={c.name}
                    onChange={(e) => {
                      const updated = activityContacts.map((ct, i) => i === idx ? { ...ct, name: e.target.value } : ct);
                      setActivityContacts(updated);
                    }}
                    placeholder="Name"
                    className="w-32 min-w-0 px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
                    data-testid={`edit-contact-name-${idx}`}
                  />
                  <button
                    type="button"
                    onClick={() => setActivityContacts(activityContacts.filter((_, i) => i !== idx))}
                    className="text-gray-300 hover:text-red-400 transition-colors flex-shrink-0"
                    data-testid={`edit-contact-remove-${idx}`}
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setActivityContacts([...activityContacts, { name: '', phone: '' }])}
                className="text-sm text-green-700 font-medium hover:underline"
                data-testid="edit-contact-add-btn"
              >
                + Add Contact
              </button>
            </div>
          ) : activityContacts.length > 0 ? (
            <div className="space-y-2">
              {activityContacts.filter((c) => c.name || c.phone).map((c, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-700">
                    {c.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <span className="text-sm text-gray-700">{c.name}</span>
                  {c.phone && (
                    <a
                      href={`tel:+91${c.phone}`}
                      className="text-sm text-green-700 font-medium hover:underline"
                    >
                      +91 {c.phone}
                    </a>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">No contacts added yet.</p>
          )}
        </div>

        {/* ══ Section 4: Verify Participants ══════════════════════════════════ */}
        {activityId && initialActivity.activity_journey !== false && (
          <VerifyParticipantsSection
            activityId={activityId}
            activitySlug={activitySlug}
            verificationEnabled={verificationEnabled}
            setVerificationEnabled={setVerificationEnabled}
            verificationType={verificationType}
            setVerificationType={setVerificationType}
            isEditMode={isEditMode}
            locIsOnline={locIsOnline}
            hasMapInfo={!!(locMapLink || (initialActivity?.location?.latitude != null && initialActivity?.location?.longitude != null))}
            qrFetchKey={qrFetchKey}
            inspireCardType={inspireCardType}
            setInspireCardType={setInspireCardType}
            eventPhotoUrl={eventPhotoUrl}
            setEventPhotoUrl={setEventPhotoUrl}
            activityDate={initialActivity.date || null}
            isAdmin={isAdmin}
          />
        )}

        {/* ══ Section 5: Publish & Share ════════════════════════════════════ */}
        <div ref={publishRef} className="px-4 pt-8 pb-8 space-y-5 bg-white border-t border-gray-200">
          <h2 className="text-base font-bold text-gray-800">Publish &amp; Share</h2>

          {/* ── Action bar — docks here once user scrolls to this section ── */}
          <div ref={actionBarRef}>
            <div className={`rounded-2xl border border-gray-200 bg-gray-50 shadow-sm overflow-hidden transition-opacity ${barIsFixed ? 'invisible' : ''}`}>
              <div className="flex gap-3 p-3">
                {/* Save */}
                <button
                  onClick={handleSave}
                  disabled={saveStatus === 'saving'}
                  data-testid="ep-save-btn"
                  className={`flex-1 flex items-center justify-center gap-1.5 py-3.5 rounded-xl text-sm font-bold border transition-all ${
                    saveStatus === 'saving'
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white border-gray-300 text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  {saveStatus === 'saving' && <Loader size={13} className="animate-spin" />}
                  {saveStatus === 'saved' ? '✓ Saved' : 'Save'}
                </button>
                {/* Publish */}
                <button
                  onClick={handlePublish}
                  disabled={publishing}
                  data-testid="ep-publish-btn"
                  className={`flex-1 flex items-center justify-center gap-1.5 py-3.5 rounded-xl text-sm font-bold transition-all ${
                    publishing
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                >
                  {publishing && <Loader size={13} className="animate-spin" />}
                  Publish
                </button>
                {/* Edit / Cancel */}
                {isEditMode ? (
                  <button
                    onClick={exitEditMode}
                    data-testid="ep-edit-mode-btn"
                    className="flex-1 flex items-center justify-center py-3.5 rounded-xl text-sm font-bold border border-red-200 text-red-600 bg-white hover:bg-red-50 transition-all"
                  >
                    Cancel
                  </button>
                ) : (
                  <button
                    onClick={enterEditMode}
                    data-testid="ep-edit-mode-btn"
                    className="flex-1 flex items-center justify-center py-3.5 rounded-xl text-sm font-bold border border-gray-300 text-gray-800 bg-white hover:bg-gray-50 transition-all"
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>
          </div>

          {publishError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-sm text-red-600">{publishError}</p>
            </div>
          )}

          {/* Share section */}
          <div className="space-y-3">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Share</p>

            <p className={`text-sm ${isPublished ? 'text-green-700' : 'text-gray-500'}`}>
              {isPublished
                ? 'Page is published. Share with others.'
                : 'This page is in preview mode. Share with others only after you Publish it.'}
            </p>

            <div className="flex flex-wrap gap-2">
              {/* WhatsApp share */}
              <button
                onClick={() => {
                  if (!isPublished) return;
                  const t = encodeURIComponent(`Check out this activity: ${name}\n${pageUrl}`);
                  openWhatsApp(t);
                }}
                disabled={!isPublished}
                data-testid="ep-whatsapp-share"
                title={!isPublished ? 'Publish first to share' : 'Share on WhatsApp'}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isPublished ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                <Globe size={15} />
                Share on WhatsApp
              </button>

              {/* Generic share / copy link */}
              <button
                onClick={async () => {
                  if (!isPublished) return;
                  if (navigator.share) { try { await navigator.share({ title: name, url: pageUrl }); return; } catch {} }
                  try { await navigator.clipboard.writeText(pageUrl); toast.success('Link copied!'); } catch { toast.error('Could not copy link'); }
                }}
                disabled={!isPublished}
                data-testid="ep-generic-share"
                title={!isPublished ? 'Publish first to share' : 'Copy link'}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isPublished ? 'border border-gray-200 text-gray-700 hover:bg-gray-50' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                <Share2 size={15} />
                Share
              </button>

              {/* View public preview — opens clean public URL in new tab */}
              <button
                onClick={() => { if (isPublished) window.open(pageUrl, '_blank'); }}
                disabled={!isPublished}
                data-testid="ep-preview-btn"
                title={!isPublished ? 'Publish first to preview' : 'View public page'}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isPublished ? 'border border-gray-200 text-gray-700 hover:bg-gray-50' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                <Globe size={15} />
                View Public Preview
              </button>
            </div>

            {/* QR code */}
            <QRSection url={pageUrl} slug={activitySlug} />
          </div>
        </div>


      </div>{/* end contentRef */}

      {/* ── Fixed floating action bar — visible while natural position is off-screen ── */}
      {barIsFixed && (
        <div
          data-testid="ep-sticky-bar"
          className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-100 shadow-[0_-4px_16px_rgba(0,0,0,0.08)]"
        >
          <div className="max-w-[624px] mx-auto px-4 py-3">
            <div className="flex gap-3">
              {/* Save */}
              <button
                onClick={handleSave}
                disabled={saveStatus === 'saving'}
                data-testid="ep-save-btn"
                className={`flex-1 flex items-center justify-center gap-1.5 py-3.5 rounded-xl text-sm font-bold border transition-all ${
                  saveStatus === 'saving'
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'bg-white border-gray-300 text-gray-800 hover:bg-gray-50'
                }`}
              >
                {saveStatus === 'saving' && <Loader size={13} className="animate-spin" />}
                {saveStatus === 'saved' ? '✓ Saved' : 'Save'}
              </button>
              {/* Publish */}
              <button
                onClick={handlePublish}
                disabled={publishing}
                data-testid="ep-publish-btn"
                className={`flex-1 flex items-center justify-center gap-1.5 py-3.5 rounded-xl text-sm font-bold transition-all ${
                  publishing
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-900 hover:bg-gray-800 text-white'
                }`}
              >
                {publishing && <Loader size={13} className="animate-spin" />}
                Publish
              </button>
              {/* Edit / Cancel */}
              {isEditMode ? (
                <button
                  onClick={exitEditMode}
                  data-testid="ep-edit-mode-btn"
                  className="flex-1 flex items-center justify-center py-3.5 rounded-xl text-sm font-bold border border-red-200 text-red-600 bg-white hover:bg-red-50 transition-all"
                >
                  Cancel
                </button>
              ) : (
                <button
                  onClick={enterEditMode}
                  data-testid="ep-edit-mode-btn"
                  className="flex-1 flex items-center justify-center py-3.5 rounded-xl text-sm font-bold border border-gray-300 text-gray-800 bg-white hover:bg-gray-50 transition-all"
                >
                  Edit
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>

    {/* ── Onboarding Tutorial Overlay (steps 7–11) ─────────────────────── */}
    {editTutorialStep !== null && (
      <ActivityEditTutorial
        activityId={activityId}
        activitySlug={activitySlug}
        step={editTutorialStep}
        onStepChange={setEditTutorialStep}
      />
    )}

    {/* ── Update Email Modal ──────────────────────────────────────────────── */}
    {emailModalUpdate && (
      <UpdateEmailModal
        update={emailModalUpdate}
        activityId={activityId}
        onClose={() => setEmailModalUpdate(null)}
        onSent={(updateId, sentAt) => {
          setActivityUpdates(prev => prev.map(u =>
            u.id === updateId ? { ...u, email_sent_at: sentAt } : u
          ));
          setEmailModalUpdate(null);
        }}
      />
    )}

    {/* ── Google Forms Sync Modal ──────────────────────────────────────────── */}
    {syncModalOpen && syncModalData && (
      <GoogleFormsSyncModal
        headers={syncModalData.headers}
        suggestedMapping={syncModalData.suggestedMapping}
        tempToken={syncModalData.tempToken}
        activityId={initialActivity.id}
        onConfirm={handleConfirmSync}
        onClose={() => { setSyncModalOpen(false); setSyncModalData(null); }}
        loading={syncLoading}
      />
    )}

    {/* ── Google Forms Picker Modal (when auto-detection fails) ─────────────── */}
    {formsPickerData && (
      <GoogleFormsPickerModal
        forms={formsPickerData.forms}
        loading={false}
        onSelect={handleFormPickerSelect}
        onClose={() => setFormsPickerData(null)}
      />
    )}

    {/* ── Collaborator Invite Modal (post-publish for unclaimed/new orgs) ────── */}
    {showInviteModal && pendingInviteCollabs.length > 0 && (
      <CollaboratorInviteModal
        activityId={activityId}
        collaborators={pendingInviteCollabs}
        onClose={() => { setShowInviteModal(false); setPendingInviteCollabs([]); }}
      />
    )}
    </>
  );
}
